/*==============================================================================
  
  ------------------------------------------------------------------------------

  # DEVELOPMENT INFO

  ### EVENT DELEGATION
  - We utilise the Dom Delegate library in `vendor.js` to better manage event
    delegation.
  - For more info:
    https://github.com/Financial-Times/ftdomdelegate

  ### FUNCTION NAMING
  - Module names are capitalised e.g. `HeaderMain`
  - Functions/methods within modules are camelCase and include an underscore 
    if they are a private method. E.g:
    `_privateFunction` vs. `publicFunction`


  ------------------------------------------------------------------------------

  # TABLE OF CONTENTS

  # UTILITIES
  - __defProp                    public field declaration
  - delegate_default             A global variable to tap into the Dom Delegate library
  - supportsAnimationsAPI        A variable to detect support for Web Animations API
  - Input Binding Manager        Keeps values in sync across various elements
  - Focus Visible Polyfill       Polyfill for focus:visible
  - Trigger Event Helpers        Helpers for triggering events
  - Shopify Event Helpers        Helper to recognise Shopify events in theme editor
  - Throttle & Debounce Helpers  Simple throttle and debounce utilities
  - Media Features Helper        Helpers to detect media features
  - DOM Helpers                  getSiblings and resolveAsyncIterator
  - Image Helpers                getSizedImageUrl, getMediaSrcset, getSupportedSizes, 
                                 imageLoaded
  - Currency                     formatMoney and currency conversion utilities
  - Product Loader               Fetch utility to getShopify products
  - Library Loader               Fetch utility to conditionally load scripts
  - QR Code                      QR Code loader for Shopify Gift Card template

  # ELEMENTS
  - Custom HTML Element          A baseline custom web component used as a basis for
                                 multiple custom elements
  - CurrencySelector             Allows showing different currencies based on exchange rates
  - Loading Bar                  Simple loading bar component
  - Openable Element             Used for openable elements such as drawers, popovers, 
                                 modals, etc
  - Loader Button Mixin          Mixin used as prototype for many buttons
  - Loader Button                Used for add to cart and form submissions
  - Toggle Button                Used for toggling open/close various elements
  - Toggle Link                  Link variation of toggle button
  - Confirm Button               Provides confirmation alert to users

  - Get Sticky Header Offset     Utility to determine offset to sticky header
  - Collapsible Content          Collapsibles component
  - Expandable Content           Allows view more or view less on elements
  - Modal Content                Modal component
  - Safe Sticky                  Advanced sticky component that will scroll a
                                 sticky element even if it is taller than viewport
  - Scroll Shadow                Provide visual indicator to scrollable elements
  - Prev Next Buttons            Arrow button component used widely
  - Combo Box                    Custom select functionality which appears as a
                                 popover on mobile
  - Popover Content              Popover component generally used for mobile
  - Drawer Content               Drawer component used across the site
  - Country Selector             Utility to show provinces on Shopify address forms
  - Quantity Selector            Simple quantity selector component
  - Input Number                 Used in conjunction with quantity selector component
  - Product Facet                Handles filters, pagination, and sort by triggered
                                 functionality and rendering of collection and search
  - Facet Filters                Handles filter functionality
  - Sort By Popover              Handles sort by functionality
  - Page Pagination              Handles AJAX pagination
  - QuickBuy Drawer              QuickBuy for larger screens
  - QuickBuy Popover             QuickBuy for small screens

  - Announcement Bar             Announcement bar component
  - Header Main                  Main header component
  - Predictive Search            Custom integration with FastSimon app
  - SearchPage                   Custom integration with FastSimon app
  - Desktop Navigation           Dropdown and mega menu functionality
  - Mobile Navigation            Drawer for mobile navigation

  - Product Media                Handles product media functionality
  - External Video               External video component
  - Native Video                 Internal video component
  - Product Meta                 Product meta component for price and SKU
  - Product Variants             Advanced product variants functionality
  - Product Payment Container    Buy buttons
  - Overflow Scroller            Used in our ProductInfo component
  - Product Info                 Handles sticky product info
  - Product Form                 Handles Add to Cart functionality

  - Cart Count                   Updates cart count
  - Line Item                    Handles loader for line item quantity changes
  - Line Item Quantity           Handles line item quantity changes
  - Cart Notification            Unused cart notification message
  - Cart Drawer                  Mini Cart drawer
  - Shipping Estimator           Shipping Estimator for Cart page

  - Product List                 Handles view mode and allows for turning into a carousel
  - Collection List              Handles carousel functionality for collection lists


  # DOM READY
  - InputBindingManager          Instantiate InputBindingManager
  - Window Height                Get window height and store as CSS variable
  - Floating Form labels         Adds classes for floating form labels
  - RTE Utilities                Add wrappers to RTE tables and videos
  - Tabbing Focus Utility        Checks for tabbing

==============================================================================*/


  /*==============================================================================
    ## Utilities
  ==============================================================================*/

  var __defProp = Object.defineProperty;

  var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;

  var __publicField = (obj, key, value) => {
    __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
    return value;
  };


  /**
   * A simple default variable to hook into the Delegate library
   */
  var delegate_default = Delegate;


  /**
   * Simple way to detect AnimationsAPI. We have some non-important animations
   * that are best achieved in JS. This variable helps ensure the animations
   * don't run for older browsers.
   */
  var supportsAnimationsAPI = 'animate' in document.body;


  /**
   * =============================================================
   * ## INPUT BINDING MANAGER
   *    Used to keep values in sync across various elements. For example
   *    if using a product sticky form, we want both the main form and
   *    sticky form to be in sync dependning on user choice.
   * =============================================================
   */

  var InputBindingManager = class {
    constructor() {
      this.delegateElement = new delegate_default(document.body);
      this.delegateElement.on("change", "[data-bind-value]", this._onValueChanged.bind(this));
    }

    _onValueChanged(event, target) {
      const boundElement = document.getElementById(target.getAttribute("data-bind-value"));
      if (boundElement) {
        if (target.tagName === "SELECT") {
          target = target.options[target.selectedIndex];
        }
        boundElement.innerHTML = target.hasAttribute("title") ? target.getAttribute("title") : target.value;
      }
    }
  };








  /**
   * =============================================================
   * ## Focus Visible Polyfill
   * =============================================================
   */

  // Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
  try {
    document.querySelector(":focus-visible");
  } catch(e) {
    focusVisiblePolyfill();
  }

  function focusVisiblePolyfill() {
    const navKeys = ['ARROWUP', 'ARROWDOWN', 'ARROWLEFT', 'ARROWRIGHT', 'TAB', 'ENTER', 'SPACE', 'ESCAPE', 'HOME', 'END', 'PAGEUP', 'PAGEDOWN']
    let currentFocusedElement = null;
    let mouseClick = null;

    window.addEventListener('keydown', (event) => {
      if(navKeys.includes(event.code.toUpperCase())) {
        mouseClick = false;
      }
    });

    window.addEventListener('mousedown', (event) => {
      mouseClick = true;
    });

    window.addEventListener('focus', () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focus-visible');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focus-visible');

    }, true);
  }










  /**
   * =============================================================
   * ## Trigger Event Helper
   * =============================================================
   */

  function triggerEvent(element, name, data = {}) {
    element.dispatchEvent(new CustomEvent(name, {
      bubbles: true,
      detail: data
    }));
  }

  function triggerNonBubblingEvent(element, name, data = {}) {
    element.dispatchEvent(new CustomEvent(name, {
      bubbles: false,
      detail: data
    }));
  }










  /**
   * =============================================================
   * ## filterShopifyEvent
   * A helper to recognise Shopify events in the Theme Editor
   * =============================================================
   */
  function filterShopifyEvent(event, domElement, callback) {
    let executeCallback = false;
    if (event.type.includes("shopify:section")) {
      if (domElement.hasAttribute("section") && domElement.getAttribute("section") === event.detail.sectionId) {
        executeCallback = true;
      }
    } else if (event.type.includes("shopify:block") && event.target === domElement) {
      executeCallback = true;
    }
    if (executeCallback) {
      callback(event);
    }
  }









  /**
   * =============================================================
   * ## Throttle & Debounce Helper
   * =============================================================
   */

  function throttle(callback, delay3 = 15) {
    let throttleTimeout = null, storedEvent = null;

    const throttledEventHandler = (event) => {
      storedEvent = event;
      const shouldHandleEvent = !throttleTimeout;
      if (shouldHandleEvent) {
        callback(storedEvent);
        storedEvent = null;
        throttleTimeout = setTimeout(() => {
          throttleTimeout = null;
          if (storedEvent) {
            throttledEventHandler(storedEvent);
          }
        }, delay3);
      }
    };
    return throttledEventHandler;
  }

  function debounce(fn, delay3) {
    let timer = null;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        fn.apply(this, args);
      }, delay3);
    };
  }










  /**
   * =============================================================
   * ## Media Features Helper
   * =============================================================
   */

  var MediaFeatures = class {
    static prefersReducedMotion() {
      return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    }
    static supportsHover() {
      return window.matchMedia("(pointer: fine)").matches;
    }
  };










  /**
   * =============================================================
   * ## DOM helpers
   * 
   * 1. getSiblings
   * 2. resolveAsyncIterator
   * =============================================================
   */

  function getSiblings(element, filter, includeSelf = false) {
    let siblings = [];
    let currentElement = element;
    while (currentElement = currentElement.previousElementSibling) {
      if (!filter || currentElement.matches(filter)) {
        siblings.push(currentElement);
      }
    }
    if (includeSelf) {
      siblings.push(element);
    }
    currentElement = element;
    while (currentElement = currentElement.nextElementSibling) {
      if (!filter || currentElement.matches(filter)) {
        siblings.push(currentElement);
      }
    }
    return siblings;
  }

  async function resolveAsyncIterator(target) {
    const processedTarget = [];
    if (!(target != null && typeof target[Symbol.iterator] === "function")) {
      target = [target];
    }
    for (const targetItem of target) {
      if (typeof targetItem[Symbol.asyncIterator] === "function") {
        for await (const awaitTarget of targetItem) {
          processedTarget.push(awaitTarget);
        }
      } else {
        processedTarget.push(targetItem);
      }
    }
    return processedTarget;
  }










  /**
   * =============================================================
   * ## IMAGE HELPERS
   * =============================================================
   */

  function getSizedMediaUrl(media, size) {
    let src = typeof media === "string" ? media : media["preview_image"] ? media["preview_image"]["src"] : media["url"];

    if (size === null) {
      return src;
    }

    if (size === "master") {
      return src.replace(/http(s)?:/, "");
    }

    src = src.replace(/_(pico|icon|thumb|small|compact|medium|large|grande|original|1024x1024|2048x2048|master)+\./g, '.');

    const match = src.match(/\.(jpg|jpeg|gif|png|bmp|bitmap|tiff|tif|webp)(\?v=\d+)?$/i);
    if (match) {
      const prefix = src.split(match[0]);
      const suffix = match[0];
      return (prefix[0] + "_" + size + suffix).replace(/http(s)?:/, "");
    } else {
      return null;
    }
  }

  function getMediaSrcset(media, sizeList) {
    let srcset = [], supportedSizes = typeof media === "string" ? sizeList : getSupportedSizes(media, sizeList);
    supportedSizes.forEach((supportedSize) => {
      srcset.push(`${getSizedMediaUrl(media, supportedSize + "x")} ${supportedSize}w`);
    });
    return srcset.join(",");
  }

  function getSupportedSizes(media, desiredSizes) {
    let supportedSizes = [], mediaWidth = media["preview_image"]["width"];
    desiredSizes.forEach((width) => {
      if (mediaWidth >= width) {
        supportedSizes.push(width);
      }
    });
    return supportedSizes;
  }

  function imageLoaded(image) {
    return new Promise((resolve) => {
      if (!image || image.tagName !== "IMG" || image.complete) {
        resolve();
      } else {
        image.onload = () => resolve();
      }
    });
  }










  /**
   * =============================================================
   * ## Currency Utilities
   * =============================================================
   */
  function formatMoney(cents, format = "") {
    if (typeof cents === "string") {
      cents = cents.replace(".", "");
    }

    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/, formatString = format || window.themeVariables.settings.moneyFormat;

    function defaultTo(value2, defaultValue) {
      return value2 == null || value2 !== value2 ? defaultValue : value2;
    }

    function formatWithDelimiters(number, precision, thousands, decimal) {
      precision = defaultTo(precision, 2);
      thousands = defaultTo(thousands, ",");
      decimal = defaultTo(decimal, ".");
      if (isNaN(number) || number == null) {
        return 0;
      }

      number = (number / 100).toFixed(precision);
      let parts = number.split("."), dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, "$1" + thousands), centsAmount = parts[1] ? decimal + parts[1] : "";
      return dollarsAmount + centsAmount;
    }

    let value = "";

    switch (formatString.match(placeholderRegex)[1]) {
      case "amount":
        value = formatWithDelimiters(cents, 2);
        break;
      case "amount_no_decimals":
        value = formatWithDelimiters(cents, 0);
        break;
      case "amount_with_space_separator":
        value = formatWithDelimiters(cents, 2, " ", ".");
        break;
      case "amount_with_comma_separator":
        value = formatWithDelimiters(cents, 2, ".", ",");
        break;
      case "amount_with_apostrophe_separator":
        value = formatWithDelimiters(cents, 2, "'", ".");
        break;
      case "amount_no_decimals_with_comma_separator":
        value = formatWithDelimiters(cents, 0, ",", ".");
        break;
      case "amount_no_decimals_with_space_separator":
        value = formatWithDelimiters(cents, 0, " ");
        break;
      case "amount_no_decimals_with_apostrophe_separator":
        value = formatWithDelimiters(cents, 0, "'");
        break;
    }

    if (formatString.indexOf("with_comma_separator") !== -1) {
      return formatString.replace(placeholderRegex, value);
    } else {
      return formatString.replace(placeholderRegex, value);
    }
  }


  /**
   * We use shopify-hosted currencies.js to get exchange rates and build a
   * `exchange_rates` variable in the theme.liquid file. This helps us build
   * a `currencies` object.
   */
  var currencies = {};

  function addCurrency(code, symbol, locale, rounding) {
    currencies[code] = {
      code: code,
      symbol: symbol,
      locale: locale,
      rounding: rounding
    }
  }

  addCurrency("GBP", "&pound;", "en", 0.01);
  addCurrency("USD", "$", "en", 0.01);
  addCurrency("CAD", "$", "ca", 0.01);
  addCurrency("EUR", "&euro;", "de", 0.01);

  /**
   * This uses our `currencies` object to format currencies. This is a simpler
   * implementation than the `formatMoney` function earlier in the code.
   */
  function currencyConversionFormatMoney(amount, newCurrency) {
    var currency = currencies[newCurrency];

    amount = (Math.round((amount * exchange_rates[currency.code]) / currency.rounding) * currency.rounding).toLocaleString(currency.locale, {minimumFractionDigits: 2, maximumFractionDigits: 2});

    return currency.symbol + amount.replace(/[.,]00$/, "");
  }

  /**
   * This handles currency conversion in the DOM. It allows passing a selector
   * so we don't always need to run this on every element. 
   */

  function currencyConversionUpdatePrices(selector = false, newCurrency) {

    (selector || document).querySelectorAll('.price--convertible').forEach((item) => {
      let amount = item.getAttribute("data-base-amount");

      /**
       * We store currency selections in localStorage 
       */
      newCurrency = newCurrency || localStorage.getItem("currency");

      item.innerHTML = `${currencyConversionFormatMoney(amount, newCurrency)}`;
    });
  }










  /**
   * =============================================================
   * ## PRODUCT LOADER
   * =============================================================
   */
  var ProductLoader = class {
    static load(productHandle) {
      if (!productHandle) {
        return;
      }

      if (this.loadedProducts[productHandle]) {
        return this.loadedProducts[productHandle];
      }

      this.loadedProducts[productHandle] = new Promise(async (resolve) => {
        const response = await fetch(`${window.themeVariables.routes.rootUrlWithoutSlash}/products/${productHandle}.js`);
        const responseAsJson = await response.json();
        resolve(responseAsJson);
      });

      return this.loadedProducts[productHandle];
    }
  };

  __publicField(ProductLoader, "loadedProducts", {});











  /**
   * =============================================================
   * ## LIBRARY LOADER
   * =============================================================
   */
  var LibraryLoader = class {
    static load(libraryName) {
      const STATUS_REQUESTED = "requested";
      const STATUS_LOADED = "loaded";
      const library = this.libraries[libraryName];

      if (!library) {
        return;
      }

      if (library.status === STATUS_REQUESTED) {
        return library.promise;
      }

      if (library.status === STATUS_LOADED) {
        return Promise.resolve();
      }

      let promise;
      if (library.type === "script") {
        promise = new Promise((resolve, reject) => {
          let tag = document.createElement("script");
          tag.id = library.tagId;
          tag.src = library.src;
          tag.onerror = reject;
          tag.onload = () => {
            library.status = STATUS_LOADED;
            resolve();
          };
          document.body.appendChild(tag);
        });
      } else {
        promise = new Promise((resolve, reject) => {
          let tag = document.createElement("link");
          tag.id = library.tagId;
          tag.href = library.src;
          tag.rel = "stylesheet";
          tag.type = "text/css";
          tag.onerror = reject;
          tag.onload = () => {
            library.status = STATUS_LOADED;
            resolve();
          };
          document.body.appendChild(tag);
        });
      }

      library.promise = promise;
      library.status = STATUS_REQUESTED;
      return promise;
    }
  };

  __publicField(LibraryLoader, "libraries", {
    qrCode: {
      tagId: "qrCode",
      src: window.themeVariables.libs.qrCode,
      type: "script"
    }
  });










  /**
   * =============================================================
   * ## QR CODE
   * =============================================================
   */
  var QrCode = class extends HTMLElement {
    async connectedCallback() {
      await LibraryLoader.load("qrCode");
      new window.QRCode(this, {
        text: this.getAttribute("identifier"),
        width: 200,
        height: 200
      });
    }
  };

  window.customElements.define("qr-code", QrCode);











  /**
   * =============================================================
   * ## CustomHTMLElement
   * 
   * A custom implementation of HTMLElements which includes
   * 1. Variable to indicate a section has been reloaded in Shopify theme editor
   * 2. Storing rootDelegate and delegate of elements
   * 3. Trigger events to show and hide a loading bar
   * 4. A unique disconnectedCallback to disconnect the previous functions
   * =============================================================
   */

  var CustomHTMLElement = class extends HTMLElement {
    constructor() {
      super();

      this._hasSectionReloaded = false;

      if (Shopify.designMode) {
        this.rootDelegate.on("shopify:section:select", (event) => {
          const parentSection = this.closest(".shopify-section");
          if (event.target === parentSection && event.detail.load) {
            this._hasSectionReloaded = true;
          }
        });
      }
    }

    get rootDelegate() {
      return this._rootDelegate = this._rootDelegate || new delegate_default(document.documentElement);
    }

    get delegate() {
      return this._delegate = this._delegate || new delegate_default(this);
    }

    showLoadingBar() {
      triggerEvent(document.documentElement, "theme:loading:start");
    }

    hideLoadingBar() {
      triggerEvent(document.documentElement, "theme:loading:end");
    }

    disconnectedCallback() {
      this.delegate.destroy();
      this.rootDelegate.destroy();
      delete this._delegate;
      delete this._rootDelegate;
    }
  };










  /**
   * =============================================================
   * ## CURRENCY SELECTOR
   * This handles our currency selectors. We are not using native multi-currency
   * and therefore showing estimated conversions based on Shopify exchange rates.
   * =============================================================
   */

  /**
   * The following are the taxable countries based on Shopify tax settings. If shipping countries are added or removed, we need to manually update this.
   */
  const taxableCountries = ['GB','AD', 'AT', 'BE', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FO', 'FI', 'FR', 'DE', 'GI', 'GR', 'HU', 'IS', 'IE', 'IT', 'LV', 'LI', 'LT', 'LU', 'YT', 'MC', 'NL', 'NO', 'PL', 'PT', 'RO', 'SM', 'SK', 'SI', 'ES', 'SJ', 'SE', 'CH', 'TR', 'VA', 'GG', 'IM', 'JE'];

  var CurrencySelector = class extends CustomHTMLElement {
    connectedCallback() {
      // set currency based on localStorage
      this.shopCurrency = window.themeVariables.settings.shopCurrency;
      this.storedCurrency = localStorage.getItem("currency");
      this.popoverX = this.querySelector(".popover-x");

      this.popoverButtonText = Array.from(document.querySelectorAll(".popover-button__text"));
      this.options = Array.from(this.querySelectorAll(".popover__choice-item"));
      this.labels = Array.from(document.querySelectorAll(".popover__choice-label"));
      this.delegate.on("click", ".popover__choice-item", this._onValueClicked.bind(this));


      /**
       * If no currency is saved in localStorage, we set it to the shopCurrency
       * so we have a hook for other functionality. 
       */
      if (!this.storedCurrency) {
        localStorage.setItem('currency', this.shopCurrency);
        this.storedCurrency = this.shopCurrency;
      }

      /**
       * User-selected currency is saved in localStorage. On page load, we check
       * if their currency is not GBP, and then run functionality to select
       * their preferred currency and run conversions.
       */
      if (this.storedCurrency !== this.shopCurrency) {
        this._selectCurrency(this.storedCurrency);
      }

      /**
       * Check whether we are in a taxable region or not.
       */
      if(taxableCountries.includes(window.Shopify.country)) {
        document.documentElement.removeAttribute("data-not-vatable");
      } else {
        document.documentElement.setAttribute("data-not-vatable", "");
      }
    }

    _selectCurrency(selectedCurrency) {
      this.popoverButtonText.forEach((element) => {
        element.innerHTML = selectedCurrency;
      });

      this.labels.forEach((element) => {
        element.removeAttribute("aria-current");
      });

      document.querySelectorAll('[data-currency-code="' + selectedCurrency + '"] .popover__choice-label').forEach((element) => {
        element.setAttribute("aria-current", "true");
      });

      currencyConversionUpdatePrices(false, selectedCurrency);

      if(taxableCountries.includes(window.Shopify.country)) {
        document.documentElement.removeAttribute("data-not-vatable");
      } else {
        document.documentElement.setAttribute("data-not-vatable", "");
      }
    }

    _onValueClicked(event, target) {
      this._selectCurrency(target.value);
      this.popoverX.open = false;
      localStorage.setItem("currency", target.value);

      triggerEvent(this, "currency:changed", { currency: target.value });
    }
  };

  window.customElements.define("currency-selector", CurrencySelector);










  /**
   * =============================================================
   * ## LOADING BAR
   * =============================================================
   */

  var LoadingBar = class extends CustomHTMLElement {
    constructor() {
      super();
      this.rootDelegate.on("theme:loading:start", this.show.bind(this));
      this.rootDelegate.on("theme:loading:end", this.hide.bind(this));
      this.delegate.on("transitionend", this._onTransitionEnd.bind(this));
    }

    show() {
      this.classList.add("is-visible");
      this.style.transform = "scaleX(0.4)";
    }
    
    hide() {
      this.style.transform = "scaleX(1)";
      this.classList.add("is-finished");
    }

    _onTransitionEnd(event) {
      if (event.propertyName === "transform" && this.classList.contains("is-finished")) {
        this.classList.remove("is-visible");
        this.classList.remove("is-finished");
        this.style.transform = "scaleX(0)";
      }
    }
  };

  window.customElements.define("loading-bar", LoadingBar);










  /**
   * =============================================================
   * ## OPENABLE ELEMENT
   * =============================================================
   */

  var OpenableElement = class extends CustomHTMLElement {
    static get observedAttributes() {
      return ["open"];
    }

    constructor() {
      super();

      // Open or close based on theme editor events
      if (Shopify.designMode) {
        this.rootDelegate.on("shopify:section:select", (event) => filterShopifyEvent(event, this, () => this.open = true));
        this.rootDelegate.on("shopify:section:deselect", (event) => filterShopifyEvent(event, this, () => this.open = false));
      }

      // if append-body, clone to body, and remove from flow
      if (this.hasAttribute("append-body")) {
        const existingNode = document.getElementById(this.id);
        this.removeAttribute("append-body");

        if (existingNode && existingNode !== this) {
          existingNode.replaceWith(this.cloneNode(true));
          this.remove();
        } else {
          document.body.appendChild(this);
        }
      }
    }

    connectedCallback() {
      // Event handlers to close
      this.delegate.on("click", ".openable__overlay", () => this.open = false);
      this.delegate.on("click", '[data-action="close"]', (event) => {
        // ensure close event does not bubble up
        event.stopPropagation();
        this.open = false;
      });
    }

    get requiresLoading() {
      return this.hasAttribute("href");
    }

    get open() {
      return this.hasAttribute("open");
    }

    set open(value) {
      if (value) {
        (async () => {
          await this._load();
          this.clientWidth;
          this.setAttribute("open", "");
        })();
      } else {
        this.removeAttribute("open");
      }
    }

    get shouldTrapFocus() {
      return true;
    }

    get returnFocusOnDeactivate() {
      return !this.hasAttribute("return-focus") || this.getAttribute("return-focus") === "true";
    }

    get focusTrap() {
      return this._focusTrap = this._focusTrap || createFocusTrap(this, {
        fallbackFocus: this,
        initialFocus: this.hasAttribute("initial-focus-selector") ? this.getAttribute("initial-focus-selector") : void 0,
        clickOutsideDeactivates: (event) => !(event.target.hasAttribute("aria-controls") && event.target.getAttribute("aria-controls") === this.id),
        allowOutsideClick: (event) => event.target.hasAttribute("aria-controls") && event.target.getAttribute("aria-controls") === this.id,
        returnFocusOnDeactivate: this.returnFocusOnDeactivate,
        onDeactivate: () => this.open = false,
        preventScroll: true
      });
    }

    attributeChangedCallback(name, oldValue, newValue) {
      switch (name) {
        case "open":
          if (oldValue === null && newValue === "") {
            if (this.shouldTrapFocus) {
              setTimeout(() => this.focusTrap.activate(), 150);
            }
            triggerEvent(this, "openable-element:open");
          } else if (newValue === null) {
            if (this.shouldTrapFocus) {
              this.focusTrap.deactivate();
            }
            triggerEvent(this, "openable-element:close");
          }
      }
    }

    async _load() {
      if (!this.requiresLoading) {
        return;
      }

      triggerNonBubblingEvent(this, "openable-element:load:start");

      const response = await fetch(this.getAttribute("href"));

      const element = document.createElement("div");
      element.innerHTML = await response.text();

      this.innerHTML = element.querySelector(this.tagName.toLowerCase()).innerHTML;

      this.removeAttribute("href");

      triggerNonBubblingEvent(this, "openable-element:load:end");
    }
  };

  window.customElements.define("openable-element", OpenableElement);










  /**
   * =============================================================
   * ## LOADER BUTTON MIXIN
   * =============================================================
   */

  var LoaderButtonMixin = {
    _prepareButton() {
      this.originalContent = this.innerHTML;
      this._startTransitionPromise = null;

      this.innerHTML = `
      <span class="loader-button__text">${this.innerHTML}</span>
      <span class="loader-button__loader" hidden>
        <div class="spinner">
          <svg focusable="false" width="24" height="24" class="icon icon--spinner" viewBox="25 25 50 50">
            <circle cx="50" cy="50" r="20" fill="none" stroke="currentColor" stroke-width="5"></circle>
          </svg>
        </div>
      </span>
    `;
      this.textElement = this.firstElementChild;
      this.spinnerElement = this.lastElementChild;
      window.addEventListener("pagehide", () => this.removeAttribute("aria-busy"));
    },

    _startTransition() {
      const textAnimation = this.textElement.animate({
        opacity: [1, 0],
        transform: ["translateY(0)", "translateY(-10px)"]
      }, {
        duration: 75,
        easing: "ease",
        fill: "forwards"
      });

      this.spinnerElement.hidden = false;

      const spinnerAnimation = this.spinnerElement.animate({
        opacity: [0, 1],
        transform: ["translate(-50%, 0%)", "translate(-50%, -50%)"]
      }, {
        duration: 75,
        delay: 75,
        easing: "ease",
        fill: "forwards"
      });

      this._startTransitionPromise = Promise.all([

        new Promise((resolve) => textAnimation.onfinish = () => resolve()),
        new Promise((resolve) => spinnerAnimation.onfinish = () => resolve())
      ]);
    },

    async _endTransition() {
      if (!this._startTransitionPromise) {
        return;
      }

      await this._startTransitionPromise;
      this.spinnerElement.animate({
        opacity: [1, 0],
        transform: ["translate(-50%, -50%)", "translate(-50%, -100%)"]
      }, {
        duration: 75,
        delay: 100,
        easing: "ease",
        fill: "forwards"
      }).onfinish = () => this.spinnerElement.hidden = true;
      this.textElement.animate({
        opacity: [0, 1],
        transform: ["translateY(10px)", "translateY(0)"]
      }, {
        duration: 75,
        delay: 175,
        easing: "ease",
        fill: "forwards"
      });
      this._startTransitionPromise = null;
    }
  };









  /**
   * =============================================================
   * ## LOADER BUTTON
   * =============================================================
   */

  var LoaderButton = class extends HTMLButtonElement {
    static get observedAttributes() {
      return ["aria-busy"];
    }

    constructor() {
      super();

      this.addEventListener("click", (event) => {
        if (this.type === "submit" && this.form && this.form.checkValidity() && !this.form.hasAttribute("is")) {
          if (/^((?!chrome|android).)*safari/i.test(navigator.userAgent)) {
            event.preventDefault();

            this.setAttribute("aria-busy", "true");
            setTimeout(() => this.form.submit(), 250);
          } else {
            this.setAttribute("aria-busy", "true");
          }
        }
      });
    }

    connectedCallback() {
      this._prepareButton();
    }

    disconnectedCallback() {
      this.innerHTML = this.originalContent;
    }

    attributeChangedCallback(property, oldValue, newValue) {
      if (property === "aria-busy" && supportsAnimationsAPI) {
        if (newValue === "true") {
          this._startTransition();
        } else {
          this._endTransition();
        }
      }
    }
  };

  Object.assign(LoaderButton.prototype, LoaderButtonMixin);

  window.customElements.define("loader-button", LoaderButton, { extends: "button" });











  /**
   * =============================================================
   * ## TOGGLE BUTTON
   * =============================================================
   */

  var ToggleButton = class extends HTMLButtonElement {
    static get observedAttributes() {
      return ["aria-expanded", "aria-busy"];
    }

    constructor() {
      super();

      if (this.hasAttribute("loader")) {
        this._prepareButton();
      }
      
      this.addEventListener("click", this._onButtonClick.bind(this));
      this.rootDelegate = new delegate_default(document.documentElement);
    }

    _onButtonClick(event) {
      this.isExpanded = !this.isExpanded;

      if (this.hasAttribute("data-media-id")) {
        this.controlledElement.setActiveMedia(this.getAttribute("data-media-id"), false);
      }
    }

    connectedCallback() {
      document.addEventListener("openable-element:close", (event) => {
        if (this.controlledElement === event.target) {
          this.isExpanded = false;
          event.stopPropagation();
        }
      });

      document.addEventListener("openable-element:open", (event) => {
        if (this.controlledElement === event.target) {
          this.isExpanded = true;
          event.stopPropagation();
        }
      });

      this.rootDelegate.on("openable-element:load:start", `#${this.getAttribute("aria-controls")}`, () => {
        if (this.classList.contains("button")) {
          this.setAttribute("aria-busy", "true");
        } else if (this.offsetParent !== null) {
          triggerEvent(document.documentElement, "theme:loading:start");
        }
      }, true);

      this.rootDelegate.on("openable-element:load:end", `#${this.getAttribute("aria-controls")}`, () => {
        if (this.classList.contains("button")) {
          this.removeAttribute("aria-busy");
        } else if (this.offsetParent !== null) {
          triggerEvent(document.documentElement, "theme:loading:end");
        }
      }, true);
    }

    disconnectedCallback() {
      this.rootDelegate.destroy();
    }

    get isExpanded() {
      return this.getAttribute("aria-expanded") === "true";
    }

    set isExpanded(value) {
      this.setAttribute("aria-expanded", value ? "true" : "false");
    }

    get controlledElement() {
      return document.getElementById(this.getAttribute("aria-controls"));
    }

    attributeChangedCallback(name, oldValue, newValue) {
      switch (name) {
        case "aria-expanded":
          if (oldValue === "false" && newValue === "true") {
            this.controlledElement.open = true;
          } else if (oldValue === "true" && newValue === "false") {
            this.controlledElement.open = false;
          }
          break;
        case "aria-busy":
          if (this.hasAttribute("loader")) {
            if (newValue === "true") {
              this._startTransition();
            } else {
              this._endTransition();
            }
          }
          break;
      }
    }
  };

  Object.assign(ToggleButton.prototype, LoaderButtonMixin);

  window.customElements.define("toggle-button", ToggleButton, { extends: "button" });










  /**
   * =============================================================
   * ## TOGGLE LINK
   * =============================================================
   */

  var ToggleLink = class extends HTMLAnchorElement {
    static get observedAttributes() {
      return ["aria-expanded"];
    }

    constructor() {
      super();
      this.addEventListener("click", (event) => {
        event.preventDefault();
        this.isExpanded = !this.isExpanded;
      });
      this.rootDelegate = new delegate_default(document.documentElement);
    }

    connectedCallback() {
      this.rootDelegate.on("openable-element:close", `#${this.getAttribute("aria-controls")}`, (event) => {
        if (this.controlledElement === event.target) {
          this.isExpanded = false;
        }
      }, true);
      this.rootDelegate.on("openable-element:open", `#${this.getAttribute("aria-controls")}`, (event) => {
        if (this.controlledElement === event.target) {
          this.isExpanded = true;
        }
      }, true);
    }

    disconnectedCallback() {
      this.rootDelegate.destroy();
    }

    get isExpanded() {
      return this.getAttribute("aria-expanded") === "true";
    }

    set isExpanded(value) {
      this.setAttribute("aria-expanded", value ? "true" : "false");
    }

    get controlledElement() {
      return document.querySelector(`#${this.getAttribute("aria-controls")}`);
    }

    attributeChangedCallback(name, oldValue, newValue) {
      switch (name) {
        case "aria-expanded":
          if (oldValue === "false" && newValue === "true") {
            this.controlledElement.open = true;
          } else if (oldValue === "true" && newValue === "false") {
            this.controlledElement.open = false;
          }
      }
    }
  };

  window.customElements.define("toggle-link", ToggleLink, { extends: "a" });











  /**
   * =============================================================
   * ## CONFIRM BUTTON
   * =============================================================
   */

  var ConfirmButton = class extends HTMLButtonElement {
    connectedCallback() {
      this.addEventListener("click", (event) => {
        if (!window.confirm(this.getAttribute("data-message") || "Are you sure you wish to do this?")) {
          event.preventDefault();
        }
      });
    }
  };

  window.customElements.define("confirm-button", ConfirmButton, { extends: "button" });










  /**
   * =============================================================
   * ## GET STICKY HEADER OFFSET
   * =============================================================
   */

  function getStickyHeaderOffset() {
    const documentStyles = getComputedStyle(document.documentElement);
    return parseInt(documentStyles.getPropertyValue("--header-height") || 0) * parseInt(documentStyles.getPropertyValue("--enable-sticky-header") || 0) + parseInt(documentStyles.getPropertyValue("--announcement-bar-height") || 0) * parseInt(documentStyles.getPropertyValue("--enable-sticky-announcement-bar") || 0);
  }










  /**
   * =============================================================
   * ## COLLAPSIBLE CONTENT
   * =============================================================
   */

  var CollapsibleContent = class extends OpenableElement {
    constructor() {
      super();
      this.ignoreNextTransition = this.open;
      this.addEventListener("shopify:block:select", () => this.open = true);
      this.addEventListener("shopify:block:deselect", () => this.open = false);
    }

    get animateItems() {
      return this.hasAttribute("animate-items");
    }

    attributeChangedCallback(name) {
      if (this.ignoreNextTransition) {
        return this.ignoreNextTransition = false;
      }

      switch (name) {
        case "open":
          if (!supportsAnimationsAPI) {
            triggerEvent(this, this.open ? "openable-element:open" : "openable-element:close");
            return;
          }

          this.style.overflow = "hidden";

          const keyframes = {
            height: ["0px", `${this.scrollHeight}px`],
            visibility: ["hidden", "visible"]
          };

          if (this.animateItems) {
            keyframes["opacity"] = this.open ? [0, 0] : [0, 1];
          }

          this.animate(keyframes, {
            duration: 500,
            direction: this.open ? "normal" : "reverse",
            easing: "cubic-bezier(0.75, 0, 0.175, 1)"
          }).onfinish = () => {
            this.style.overflow = this.open ? "visible" : "hidden";
          };

          if (this.animateItems && this.open) {
            this.animate({
              opacity: [0, 1],
              transform: ["translateY(10px)", "translateY(0)"]
            }, {
              duration: 250,
              delay: 250,
              easing: "cubic-bezier(0.75, 0, 0.175, 1)"
            });
          }

          triggerEvent(this, this.open ? "openable-element:open" : "openable-element:close");
      }
    }
  };

  window.customElements.define("collapsible-content", CollapsibleContent);











  /**
   * =============================================================
   * ## EXPANDABLE CONTENT
   * =============================================================
   */

  var ExpandableContent = class extends OpenableElement {
    constructor() {
      super();

      this.expandableToggleButton = this.querySelector(".expandable-content__toggle")
      this.expandableToggleText = this.querySelector(".expandable-content__toggle-text");
      this.expandableContentContainer = this.closest(".expandable-content__container");
    }

    connectedCallback() {
      /**
       * If the content is taller than the element height, we add a class which
       * will show the toggle button.
       */
      if (this.scrollHeight > this.clientHeight) {
        this.classList.add('expandable-content--expandable');
      }
    }

    attributeChangedCallback(name) {
      switch (name) {
        case "open":
          if (!supportsAnimationsAPI) {
            triggerEvent(this, this.open ? "openable-element:open" : "openable-element:close");
            return;
          }

          this.style.overflow = "hidden";

            // Scroll to top of opened content
            var amountToScroll = this.expandableContentContainer.getBoundingClientRect().top - 15 - parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-height'));

            window.scrollBy({
              top: amountToScroll,
              behavior: 'smooth'
            });

          const keyframes = {
            height: ["320px", `${this.scrollHeight}px`]
          };

          // Because the expandable button is still positioned absolutely, we need to add margin to the sibling
          // text to account for the button.
          if (this.expandableToggleButton.previousElementSibling) {
            this.expandableToggleButton.previousElementSibling.style['margin-bottom'] = parseInt(this.expandableToggleButton.clientHeight / 1.5) + 'px';
          }

          if (this.animateItems) {
            keyframes["opacity"] = this.open ? [0, 0] : [0, 1];
          }

          this.animate(keyframes, {
            duration: 500,
            direction: this.open ? "normal" : "reverse",
            easing: "cubic-bezier(0.75, 0, 0.175, 1)"
          }).onfinish = () => {
            this.style.overflow = this.open ? "visible" : "hidden";
          };

          triggerEvent(this, this.open ? "openable-element:open" : "openable-element:close");
      }
    }
  }

  window.customElements.define("expandable-content", ExpandableContent);










  /**
   * =============================================================
   * ## MODAL CONTENT
   * =============================================================
   */

  var ModalContent = class extends OpenableElement {
    connectedCallback() {
      super.connectedCallback();

      this.delegate.on("click", ".modal__overlay", () => this.open = false);
    }

    attributeChangedCallback(name, oldValue, newValue) {
      super.attributeChangedCallback(name, oldValue, newValue);
      switch (name) {
        case "open":
          document.documentElement.classList.toggle("lock-all", this.open);
      }
    }
  };

  window.customElements.define("modal-content", ModalContent);










  /**
   * =============================================================
   * ## Safe Sticky
   * =============================================================
   */

  var SafeSticky = class extends HTMLElement {
    connectedCallback() {
      this.lastKnownY = window.scrollY;
      this.currentTop = 0;
      this.hasPendingRaf = false;
      window.addEventListener("scroll", this._checkPosition.bind(this));
    }

    get initialTopOffset() {
      return getStickyHeaderOffset() + (parseInt(this.getAttribute("offset")) || 0);
    }

    _checkPosition() {
      if (this.hasPendingRaf) {
        return;
      }

      this.hasPendingRaf = true;

      requestAnimationFrame(() => {
        let bounds = this.getBoundingClientRect();
        let maxTop = bounds.top + window.scrollY - this.offsetTop + this.initialTopOffset; 
        let minTop = this.clientHeight - window.innerHeight;

        if (window.scrollY < this.lastKnownY) {
          this.currentTop -= window.scrollY - this.lastKnownY;
        } else {
          this.currentTop += this.lastKnownY - window.scrollY;
        }
        this.currentTop = Math.min(Math.max(this.currentTop, -minTop), maxTop, this.initialTopOffset);
        this.lastKnownY = window.scrollY;
        this.style.top = `${this.currentTop}px`;
        this.hasPendingRaf = false;
      });
    }
  };

  window.customElements.define("safe-sticky", SafeSticky);









  /**
   * =============================================================
   * ## Scroll Shadow
   * =============================================================
   */
  var template = `
  <style>
    :host {
      display: inline-block;
      contain: layout;
      position: relative;
    }
    
    :host([hidden]) {
      display: none;
    }
    
    s {
      position: absolute;
      top: 0;
      bottom: 0;
      left: 0;
      right: 0;
      pointer-events: none;
      background-image:
        var(--scroll-shadow-top, radial-gradient(farthest-side at 50% 0%, rgba(0,0,0,.2), rgba(0,0,0,0))),
        var(--scroll-shadow-bottom, radial-gradient(farthest-side at 50% 100%, rgba(0,0,0,.2), rgba(0,0,0,0))),
        var(--scroll-shadow-left, radial-gradient(farthest-side at 0%, rgba(0,0,0,.2), rgba(0,0,0,0))),
        var(--scroll-shadow-right, radial-gradient(farthest-side at 100%, rgba(0,0,0,.2), rgba(0,0,0,0)));
      background-position: top, bottom, left, right;
      background-repeat: no-repeat;
      background-size: 100% var(--top, 0), 100% var(--bottom, 0), var(--left, 0) 100%, var(--right, 0) 100%;
    }
  </style>
  <slot></slot>
  <s></s>
  `;

  var Updater = class {
    constructor(targetElement) {
      this.scheduleUpdate = throttle(() => this.update(targetElement, getComputedStyle(targetElement)));
      this.resizeObserver = new ResizeObserver(this.scheduleUpdate.bind(this));
    }

    start(element) {
      if (this.element) {
        this.stop();
      }
      if (element) {
        element.addEventListener("scroll", this.scheduleUpdate);
        this.resizeObserver.observe(element);
        this.element = element;
      }
    }

    stop() {
      if (!this.element) {
        return;
      }
      this.element.removeEventListener("scroll", this.scheduleUpdate);
      this.resizeObserver.unobserve(this.element);
      this.element = null;
    }

    update(targetElement, style) {
      if (!this.element) {
        return;
      }
      const maxSize = style.getPropertyValue("--scroll-shadow-size") ? parseInt(style.getPropertyValue("--scroll-shadow-size")) : 0;
      const scroll = {
        top: Math.max(this.element.scrollTop, 0),
        bottom: Math.max(this.element.scrollHeight - this.element.offsetHeight - this.element.scrollTop, 0),
        left: Math.max(this.element.scrollLeft, 0),
        right: Math.max(this.element.scrollWidth - this.element.offsetWidth - this.element.scrollLeft, 0)
      };
      requestAnimationFrame(() => {
        for (const position of ["top", "bottom", "left", "right"]) {
          targetElement.style.setProperty(`--${position}`, `${scroll[position] > maxSize ? maxSize : scroll[position]}px`);
        }
      });
    }
  };

  var ScrollShadow = class extends HTMLElement {
    constructor() {
      super();
      this.attachShadow({ mode: "open" }).innerHTML = template;
      this.updater = new Updater(this.shadowRoot.lastElementChild);
    }

    connectedCallback() {
      this.shadowRoot.querySelector("slot").addEventListener("slotchange", () => this.start());
      this.start();
    }

    disconnectedCallback() {
      this.updater.stop();
    }

    start() {
      this.updater.start(this.firstElementChild);
    }
  };

  if ("ResizeObserver" in window) {
    window.customElements.define("scroll-shadow", ScrollShadow);
  }










  /**
   * =============================================================
   * ## PAGE DOTS
   * =============================================================
   */

  var PageDots = class extends CustomHTMLElement {
    
    connectedCallback() {
      this.buttons = Array.from(this.querySelectorAll("button"));
      this.delegate.on("click", "button", (event, target) => {
        this._dispatchEvent(this.buttons.indexOf(target));
      });

      /**
       * This hooks into a CSS animation which triggers the page dot change,
       * and essentially allows us to use "autoplay" functionality on a 
       * carousel.
       */
      if (this.hasAttribute("animation-timer")) {
        this.delegate.on("animationend", (event) => {
          if (event.elapsedTime > 0) {
            this._dispatchEvent((this.selectedIndex + 1 + this.buttons.length) % this.buttons.length);
          }
        });
      }
    }

    get selectedIndex() {
      return this.buttons.findIndex((button) => button.getAttribute("aria-current") === "true");
    }

    set selectedIndex(selectedIndex) {
      this.buttons.forEach((button, index) => button.setAttribute("aria-current", selectedIndex === index ? "true" : "false"));
    }

    _dispatchEvent(index) {
      if (index !== this.selectedIndex) {
        this.dispatchEvent(new CustomEvent("page-dots:changed", {
          bubbles: true,
          detail: {
            index
          }
        }));
      }
    }
  };

  window.customElements.define("page-dots", PageDots);












  /**
   * =============================================================
   * ## PREV NEXT BUTTONS
   * =============================================================
   */

  var PrevNextButtons = class extends HTMLElement {
    connectedCallback() {
      this.prevButton = this.querySelector("button:first-of-type");
      this.nextButton = this.querySelector("button:last-of-type");

      this.prevButton.addEventListener("click", () => this.prevButton.dispatchEvent(new CustomEvent("prev-next:prev", { bubbles: true })));
      this.nextButton.addEventListener("click", () => this.nextButton.dispatchEvent(new CustomEvent("prev-next:next", { bubbles: true })));
    }

    set isPrevDisabled(value) {
      this.prevButton.disabled = value;
    }

    set isNextDisabled(value) {
      this.nextButton.disabled = value;
    }
  };

  var PrevButton = class extends HTMLButtonElement {
    connectedCallback() {
      this.addEventListener("click", () => this.dispatchEvent(new CustomEvent("prev-next:prev", { bubbles: true })));
    }
  };

  var NextButton = class extends HTMLButtonElement {
    connectedCallback() {
      this.addEventListener("click", () => this.dispatchEvent(new CustomEvent("prev-next:next", { bubbles: true })));
    }
  };

  window.customElements.define("prev-next-buttons", PrevNextButtons);
  window.customElements.define("prev-button", PrevButton, { extends: "button" });
  window.customElements.define("next-button", NextButton, { extends: "button" });











  /**
   * =============================================================
   * ## Native Carousel
   * =============================================================
   */

  var NativeCarousel = class extends CustomHTMLElement {

    connectedCallback() {
      this.items = Array.from(this.querySelectorAll("native-carousel-item"));
      this.pageDotsElements = Array.from(this.querySelectorAll("page-dots"));
      this.prevNextButtonsElements = Array.from(this.querySelectorAll("prev-next-buttons"));

      if (this.items.length > 1) {
        this.addEventListener("prev-next:prev", this.prev.bind(this));
        this.addEventListener("prev-next:next", this.next.bind(this));
        this.addEventListener("page-dots:changed", (event) => this.select(event.detail.index, true));

        if (Shopify.designMode) {
          this.addEventListener("shopify:block:deselect", this.startPlayer.bind(this));

          this.addEventListener("shopify:block:select", (event) => {
            this.pausePlayer();
            if (!(!event.detail.load && event.target.selected)) {
              this.select(event.target.index, !event.detail.load);
            }
          });
        }
      }

      const scrollerElement = this.items[0].parentElement;

      this.intersectionObserver = new IntersectionObserver(this._onVisibilityChanged.bind(this), { root: scrollerElement, rootMargin: `${scrollerElement.clientHeight}px 0px`, threshold: 0.8 });
      this.items.forEach((item) => this.intersectionObserver.observe(item));

      this.startPlayer();
    }

    disconnectedCallback() {
      super.disconnectedCallback();
      this.intersectionObserver.disconnect();
    }

    get selectedIndex() {
      return this.items.findIndex((item) => item.selected);
    }

    prev(shouldAnimate = true) {
      this.select(Math.max(this.selectedIndex - 1, 0), shouldAnimate);
    }

    next(shouldAnimate = true) {
      this.select(Math.min(this.selectedIndex + 1, this.items.length - 1), shouldAnimate);
    }

    select(index, shouldAnimate = true) {
      const clampIndex = Math.max(0, Math.min(index, this.items.length));
      const selectedElement = this.items[clampIndex];

      this._adjustNavigationForElement(selectedElement);

      if (shouldAnimate) {
        this.items.forEach((item) => this.intersectionObserver.unobserve(item));

        setInterval(() => {
          this.items.forEach((item) => this.intersectionObserver.observe(item));
        }, 800);
      }

      this.items.forEach((item, loopIndex) => item.selected = loopIndex === clampIndex);

      const direction = 1;

      selectedElement.parentElement.scrollTo({ left: direction * (selectedElement.clientWidth * clampIndex), behavior: shouldAnimate ? "smooth" : "auto" });
    }

    /**
     * The autoplay functionality is triggered by events on the `PageDots` element
     */
    pausePlayer() {
      this.style.setProperty("--section-animation-play-state", "paused");
    }

    startPlayer() {
      if (this.hasAttribute("autoplay")) {
        this.style.setProperty("--section-animation-play-state", "running");
      }
    }

    _adjustNavigationForElement(selectedElement) {
      this.items.forEach((item) => item.selected = selectedElement === item);
      this.pageDotsElements.forEach((pageDot) => pageDot.selectedIndex = selectedElement.index);

      this.prevNextButtonsElements.forEach((prevNextButton) => {
        prevNextButton.isPrevDisabled = selectedElement.index === 0;
        prevNextButton.isNextDisabled = selectedElement.index === this.items.length - 1;
      });
    }

    _onVisibilityChanged(entries) {
      for (let entry of entries) {
        if (entry.isIntersecting) {
          this._adjustNavigationForElement(entry.target);
          break;
        }
      }
    }
  };

  var NativeCarouselItem = class extends CustomHTMLElement {
    static get observedAttributes() {
      return ["hidden"];
    }

    get index() {
      return [...this.parentNode.children].indexOf(this);
    }

    get selected() {
      return !this.hasAttribute("hidden");
    }

    set selected(value) {
      this.hidden = !value;
    }
  };

  window.customElements.define("native-carousel-item", NativeCarouselItem);
  window.customElements.define("native-carousel", NativeCarousel);










  /**
   * =============================================================
   * ## COMBO BOX
   * - Custom Select functionality which is styled like a popover on mobile
   * =============================================================
   */

  var ComboBox = class extends OpenableElement {
    connectedCallback() {
      super.connectedCallback();

      this.options = Array.from(this.querySelectorAll('[role="option"]'));

      this.delegate.on("click", '[role="option"]', this._onValueClicked.bind(this));
      this.delegate.on("keydown", '[role="listbox"]', this._onKeyDown.bind(this));
      this.delegate.on("change", "select", this._onValueChanged.bind(this));
      this.delegate.on("click", ".combo-box__overlay", () => this.open = false);

      // fit-toggle attribute used for sticky form option if we want in future
      if (this.hasAttribute("fit-toggle")) {
        const maxWidth = Math.max(...this.options.map((item) => item.clientWidth)), control = document.querySelector(`[aria-controls="${this.id}"]`);
        if (control) {
          control.style.setProperty("--largest-option-width", `${maxWidth + 2}px`);
        }
      }
    }

    get nativeSelect() {
      return this.querySelector("select");
    }

    set selectedValue(value) {
      this.options.forEach((option) => {
        option.setAttribute("aria-selected", option.getAttribute("value") === value ? "true" : "false");
      });
    }

    attributeChangedCallback(name, oldValue, newValue) {
      super.attributeChangedCallback(name, oldValue, newValue);

      switch (name) {
        case "open":
          if (this.open) {
            const boundingRect = this.getBoundingClientRect();
            this.classList.toggle("combo-box--top", boundingRect.top >= window.innerHeight / 2 * 1.5);
            setTimeout(() => this.focusTrap.activate(), 150);
          } else {
            this.focusTrap.deactivate();
            setTimeout(() => this.classList.remove("combo-box--top"), 200);
          }

          document.documentElement.classList.toggle("lock-mobile", this.open);
      }
    }

    _onValueClicked(event, target) {
      this.selectedValue = target.value;
      this.nativeSelect.value = target.value;
      this.nativeSelect.dispatchEvent(new Event("change", { bubbles: true }));
      this.open = false;
    }

    _onValueChanged(event, target) {
      Array.from(this.nativeSelect.options).forEach((option) => option.toggleAttribute("selected", target.value === option.value));
      this.selectedValue = target.value;
    }

    _onKeyDown(event) {
      var _a, _b;

      if (event.key === "ArrowDown" || event.key === "ArrowUp") {
        event.preventDefault();

        if (event.key === "ArrowDown") {
          (_a = document.activeElement.nextElementSibling) == null ? void 0 : _a.focus();
        } else {
          (_b = document.activeElement.previousElementSibling) == null ? void 0 : _b.focus();
        }
      }
    }
  };

  window.customElements.define("combo-box", ComboBox);











  /**
   * =============================================================
   * ## POPOVER CONTENT
   * =============================================================
   */
  var PopoverContent = class extends OpenableElement {
    connectedCallback() {
      super.connectedCallback();

      this.delegate.on("click", ".popover__overlay", () => this.open = false);
    }

    attributeChangedCallback(name, oldValue, newValue) {
      super.attributeChangedCallback(name, oldValue, newValue);
      switch (name) {
        case "open":
          document.documentElement.classList.toggle("lock-mobile", this.open);
      }
    }
  };
  
  window.customElements.define("popover-content", PopoverContent);











  /**
   * =============================================================
   * ## DRAWER CONTENT
   * =============================================================
   */

  var DrawerContent = class extends OpenableElement {
    connectedCallback() {
      super.connectedCallback();

      if (this.hasAttribute("reverse-breakpoint")) {
        this.originalDirection = this.classList.contains("drawer--from-left") ? "left" : "right";

        const matchMedia2 = window.matchMedia(this.getAttribute("reverse-breakpoint"));
        matchMedia2.addListener(this._checkReverseOpeningDirection.bind(this));

        this._checkReverseOpeningDirection(matchMedia2);
      }
      this.delegate.on("click", ".drawer__overlay", () => this.open = false);
    }

    attributeChangedCallback(name, oldValue, newValue) {
      super.attributeChangedCallback(name, oldValue, newValue);
      switch (name) {
        case "open":
          document.documentElement.classList.toggle("lock-all", this.open);
      }
    }

    _checkReverseOpeningDirection(match) {
      this.classList.remove("drawer--from-left");
      if (this.originalDirection === "left" && !match.matches || this.originalDirection !== "left" && match.matches) {
        this.classList.add("drawer--from-left");
      }
    }
  };

  window.customElements.define("drawer-content", DrawerContent);










  /**
   * =============================================================
   * ## COUNTRY SELECTOR
   * =============================================================
   */

  var CountrySelector = class extends HTMLSelectElement {
    connectedCallback() {
      this.provinceElement = document.getElementById(this.getAttribute("aria-owns"));

      /**
       * #EDIT: If the country selector is in the Shipping Estimator, we define
       * some additional variables so we can change the province selector.
       */
      this.inShippingEstimator = this.closest("shipping-estimator") ? true : void 0;

      if (this.inShippingEstimator) {
        this.countrySelectElement = document.getElementById("shipping-estimator[country]");
        this.provinceSelectElement = document.getElementById("shipping-estimator[province]");
      }

      this.addEventListener("change", this._updateProvinceVisibility.bind(this));

      if (this.hasAttribute("data-default")) {
        for (let i = 0; i !== this.options.length; ++i) {
          if (this.options[i].text === this.getAttribute("data-default")) {
            this.selectedIndex = i;
            break;
          }
        }
      }

      if (this.provinceElement.hasAttribute("data-default")) {
        for (let i = 0; i !== this.provinceElement.options.length; ++i) {
          if (this.provinceElement.options[i].text === this.provinceElement.getAttribute("data-default")) {
            this.provinceElement.selectedIndex = i;
            break;
          }
        }
      }
      this._updateProvinceVisibility();

      /**
       * #EDIT: If there isn't a default province for the user, and they have
       * selected the UK country, we want to set the province to England by 
       * default, instead of British Forces. This works on the Shipping Estimator
       * only.
       */
      if (this.inShippingEstimator && !this.provinceElement.hasAttribute("data-default") && this.countrySelectElement.value === 'United Kingdom') {
        this.provinceSelectElement.value = 'England';
      }
    }

    _updateProvinceVisibility() {
      const selectedOption = this.options[this.selectedIndex];

      if (!selectedOption) {
        return;
      }

      let provinces = JSON.parse(selectedOption.getAttribute("data-provinces") || "[]");
      let provinceSelectElement = this.provinceElement.tagName === "SELECT" ? this.provinceElement : this.provinceElement.querySelector("select");

      provinceSelectElement.innerHTML = "";

      if (provinces.length === 0) {
        this.provinceElement.hidden = true;
        return;
      }

      provinces.forEach((data) => {
        provinceSelectElement.options.add(new Option(data[1], data[0]));
      });

      this.provinceElement.hidden = false;
    }
  };

  window.customElements.define("country-selector", CountrySelector, { extends: "select" });









  /**
   * =============================================================
   * ## QUANTITY SELECTOR
   * =============================================================
   */

  var QuantitySelector = class extends CustomHTMLElement {
    connectedCallback() {
      this.inputElement = this.querySelector("input");

      this.delegate.on("click", "button:first-child", () => this.inputElement.quantity = this.inputElement.quantity - 1);
      this.delegate.on("click", "button:last-child", () => this.inputElement.quantity = this.inputElement.quantity + 1);
    }
  };

  window.customElements.define("quantity-selector", QuantitySelector);










  /**
   * =============================================================
   * ## INPUT NUMBER
   * =============================================================
   */

  var InputNumber = class extends HTMLInputElement {
    connectedCallback() {
      this.addEventListener("input", this._onValueInput.bind(this));
      this.addEventListener("change", this._onValueChanged.bind(this));
      this.addEventListener("keydown", this._onKeyDown.bind(this));
    }

    get quantity() {
      return parseInt(this.value);
    }

    set quantity(quantity) {
      const isNumeric = (typeof quantity === "number" || typeof quantity === "string" && quantity.trim() !== "") && !isNaN(quantity);

      if (quantity === "") {
        return;
      }

      if (!isNumeric || quantity < 0) {
        quantity = parseInt(quantity) || 1;
      }

      this.value = Math.max(this.min || 1, Math.min(quantity, this.max || Number.MAX_VALUE)).toString();
      this.size = Math.max(this.value.length + 1, 2);
    }

    _onValueInput() {
      this.quantity = this.value;
    }

    _onValueChanged() {
      if (this.value === "") {
        this.quantity = 1;
      }
    }

    _onKeyDown(event) {
      event.stopPropagation();

      if (event.key === "ArrowUp") {
        this.quantity = this.quantity + 1;
      } else if (event.key === "ArrowDown") {
        this.quantity = this.quantity - 1;
      }
    }
  };

  window.customElements.define("input-number", InputNumber, { extends: "input" });










  /**
   * =============================================================
   * ## PRODUCT FACET
   * - Tag based filtering system
   * =============================================================
   */

  var ProductFacet = class extends CustomHTMLElement {
    connectedCallback() {
      this.delegate.on("pagination:page-changed", this._rerender.bind(this));
      this.delegate.on("facet:criteria-changed", this._rerender.bind(this));
      this.delegate.on("facet:abort-loading", this._abort.bind(this));
    }

    async _rerender(event) {
      history.replaceState({}, "", event.detail.url);
      this._abort();
      this.showLoadingBar();

      const url = new URL(window.location);

      url.searchParams.set("section_id", this.getAttribute("section-id"));
      try {
        this.abortController = new AbortController();

        const response = await fetch(url.toString(), { signal: this.abortController.signal });
        const responseAsText = await response.text();
        const fakeDiv = document.createElement("div");

        fakeDiv.innerHTML = responseAsText;

        this.querySelector("#facet-main").innerHTML = fakeDiv.querySelector("#facet-main").innerHTML;

        currencyConversionUpdatePrices(this);

        // Reinitialise reviews.io rating snippet
        reinitialiseRatingSnippet();

        const activeFilterList = Array.from(fakeDiv.querySelectorAll(".product-facet__active-list")); 
        const toolbarItem = document.querySelector(".mobile-toolbar__item--filters");

        if (toolbarItem) {
          toolbarItem.classList.toggle("has-filters", activeFilterList.length > 0);
        }

        const filtersTempDiv = fakeDiv.querySelector("#facet-filters");

        if (filtersTempDiv) {
          const previousScrollTop = this.querySelector("#facet-filters .drawer__content").scrollTop;

          Array.from(this.querySelectorAll("#facet-filters-form .collapsible-toggle")).forEach((filterToggle) => {

            const filtersTempDivToggle = filtersTempDiv.querySelector(`[aria-controls="${filterToggle.getAttribute("aria-controls")}"]`); 
            const isExpanded = filterToggle.getAttribute("aria-expanded") === "true";

            filtersTempDivToggle.setAttribute("aria-expanded", isExpanded ? "true" : "false");
            filtersTempDivToggle.nextElementSibling.toggleAttribute("open", isExpanded);
            filtersTempDivToggle.nextElementSibling.style.overflow = isExpanded ? "visible" : "";
          });

          this.querySelector("#facet-filters").innerHTML = filtersTempDiv.innerHTML;

          this.querySelector("#facet-filters .drawer__content").scrollTop = previousScrollTop;
        }

        const scrollTo = this.querySelector(".product-facet__meta-bar") || this.querySelector(".product-facet__product-list") || this.querySelector(".product-facet__main");
        requestAnimationFrame(() => {
          scrollTo.scrollIntoView({ block: "start", behavior: "smooth" });
        });

        this.hideLoadingBar();

      } catch (e) {
        if (e.name === "AbortError") {
          return;
        }
      }
    }

    _abort() {
      if (this.abortController) {
        this.abortController.abort();
      }
    }
  };

  window.customElements.define("product-facet", ProductFacet);












  /**
   * =============================================================
   * ## FACET FILTERS
   * - Tag based filtering system
   * =============================================================
   */

  var FacetFilters = class extends DrawerContent {
    connectedCallback() {
      super.connectedCallback();

      const scriptTag = this.querySelector("script");
      this.currentTags = JSON.parse(scriptTag.innerHTML);

      this.currentUrl = new URL(window.location.href);

      this.delegate.on("click", '[data-action="toggle-tag"]', this._onFilterChanged.bind(this));

      this.rootDelegate.on("click", '[data-action="clear-filters"]', this._onFiltersCleared.bind(this));

      if (this.alwaysVisible) {
        this.matchMedia = window.matchMedia(window.themeVariables.breakpoints.mediumDown);
        this.matchMedia.addListener(this._adjustDrawer.bind(this));
        this._adjustDrawer(this.matchMedia);
      }
    }

    get alwaysVisible() {
      return this.hasAttribute("always-visible");
    }

    get isAutomatic() {
      return this.hasAttribute("is-automatic");
    }

    _onFiltersCleared(event, target) {
      event.preventDefault();

      var hadTags = this.currentTags.length > 0;
      this.currentTags = [];
      this.currentUrl.searchParams.delete("page");
      this._updateFilterUrl(hadTags);
    }

    _onFilterChanged(event, target) {
      event.preventDefault();

      var newTag = target.getAttribute('data-tag');
      var hadTags = this.currentTags.length > 0;

      if (this.currentTags.includes(newTag)) {
        this.currentTags = this.currentTags.filter(function(value) {
          return value !== newTag;
        });
      } else {
        var _this = this;
        var tagGroup = newTag.split('_')[0];
        this.currentTags.forEach(function (existingTag) {
          if (existingTag.split('_')[0] === tagGroup) {
            _this.currentTags = _this.currentTags.filter(function(value) {
              return value !== existingTag;
            });
          }
        });
        this.currentTags.push(newTag);
      }

      this.currentUrl.searchParams.delete("page");

      this._updateFilterUrl(hadTags);
    }

    _updateFilterUrl(hadTags) {
      // Consider adding this for the vendor type urls
      if (this.isAutomatic) {
        if (this.currentTags.length === 0) {
          this.currentUrl.searchParams.delete("constraint");
        } else {
          this.currentUrl.searchParams.set("constraint", this.currentTags.join('+'));
        }
      } else {
        var tagPath = this.currentTags.join('+');

        var currentPathName = this.currentUrl.pathname.substr(-1) === '/' ? this.currentUrl.pathname.substr(0, this.currentUrl.pathname.length - 1) : this.currentUrl.pathname;

        if (hadTags) {
          var pathParts = currentPathName.split('/');
          pathParts.pop();
          this.currentUrl.pathname = "".concat(pathParts.join('/'), "/").concat(tagPath);
        } else {
          this.currentUrl.pathname = "".concat(currentPathName, "/").concat(tagPath);
        }
      }

      triggerEvent(this, "facet:criteria-changed", { url: this.currentUrl.toString() });
    }

    _adjustDrawer(match) {
      this.classList.toggle("drawer", match.matches);
      this.classList.toggle("drawer--from-left", match.matches);
    }
  };

  window.customElements.define("facet-filters", FacetFilters);











  /**
   * =============================================================
   * ## SORT BY POPOVER
   * =============================================================
   */

  var SortByPopover = class extends PopoverContent {
    connectedCallback() {
      super.connectedCallback();
      this.delegate.on("change", '[name="sort_by"]', this._onSortChanged.bind(this));
    }

    _onSortChanged(event, target) {
      const currentUrl = new URL(location.href);
      currentUrl.searchParams.set("sort_by", target.value);
      currentUrl.searchParams.delete("page");
      this.open = false;
      this.dispatchEvent(new CustomEvent("facet:criteria-changed", {
        bubbles: true,
        detail: {
          url: currentUrl.toString()
        }
      }));
    }
  };

  window.customElements.define("sort-by-popover", SortByPopover);










  /**
   * =============================================================
   * ## PAGE PAGINATION
   * =============================================================
   */

  var PagePagination = class extends CustomHTMLElement {
    connectedCallback() {
      if (this.hasAttribute("ajax")) {
        this.delegate.on("click", "a", this._onLinkClicked.bind(this));
      }
    }
    _onLinkClicked(event, target) {
      event.preventDefault();

      const url = new URL(window.location.href);
      url.searchParams.set("page", target.getAttribute("data-page"));
      triggerEvent(this, "pagination:page-changed", { url: url.toString() });
    }
  };

  window.customElements.define("page-pagination", PagePagination);










  /**
   * =============================================================
   * ## QUICKBUY DRAWER
   * =============================================================
   */

  var QuickBuyDrawer = class extends DrawerContent {
    connectedCallback() {
      super.connectedCallback();
      this.delegate.on("variant:changed", this._onVariantChanged.bind(this));
      // #EDIT: Close drawer on variant added
      this.delegate.on("variant:added", () => this.open = false);
    }

    async _load() {
      await super._load();
      this.imageElement = this.querySelector(".quick-buy-product__image");

      if (window.Shopify && window.Shopify.PaymentButton) {
        window.Shopify.PaymentButton.init();
      }

      currencyConversionUpdatePrices(this);
    }

    _onVariantChanged(event) {
      const variant = event.detail.variant;

      if (variant) {
        Array.from(this.querySelectorAll(`[href*="/products"]`)).forEach((link) => {
          const url = new URL(link.href);
          url.searchParams.set("variant", variant["id"]);
          link.setAttribute("href", url.toString());
        });
      }

      if (!this.imageElement || !variant || !variant["featured_media"]) {
        return;
      }

      const featuredMedia = variant["featured_media"];
      if (featuredMedia["alt"]) {
        this.imageElement.setAttribute("alt", featuredMedia["alt"]);
      }
      this.imageElement.setAttribute("width", featuredMedia["preview_image"]["width"]);
      this.imageElement.setAttribute("height", featuredMedia["preview_image"]["height"]);
      this.imageElement.setAttribute("src", getSizedMediaUrl(featuredMedia, "342x"));
      this.imageElement.setAttribute("srcset", getMediaSrcset(featuredMedia, [114, 228, 342]));
    }
  };

  window.customElements.define("quick-buy-drawer", QuickBuyDrawer);










  /**
   * =============================================================
   * ## QUICKBUY POPOVER
   * =============================================================
   */

  var QuickBuyPopover = class extends PopoverContent {
    connectedCallback() {
      super.connectedCallback();
      this.delegate.on("variant:changed", this._onVariantChanged.bind(this));
      this.delegate.on("variant:added", () => this.open = false);
    }

    async _load() {
      await super._load();
      this.imageElement = this.querySelector(".quick-buy-product__image");
      currencyConversionUpdatePrices(this);
    }

    _onVariantChanged(event) {
      const variant = event.detail.variant;
      if (variant) {
        Array.from(this.querySelectorAll(`[href*="/products"]`)).forEach((link) => {
          const url = new URL(link.href);
          url.searchParams.set("variant", variant["id"]);
          link.setAttribute("href", url.toString());
        });
      }
      if (!this.imageElement || !variant || !variant["featured_media"]) {
        return;
      }
      const featuredMedia = variant["featured_media"];
      if (featuredMedia["alt"]) {
        this.imageElement.setAttribute("alt", featuredMedia["alt"]);
      }
      this.imageElement.setAttribute("width", featuredMedia["preview_image"]["width"]);
      this.imageElement.setAttribute("height", featuredMedia["preview_image"]["height"]);
      this.imageElement.setAttribute("src", getSizedMediaUrl(featuredMedia, "195x"));
      this.imageElement.setAttribute("srcset", getMediaSrcset(featuredMedia, [65, 130, 195]));
    }
  };

  window.customElements.define("quick-buy-popover", QuickBuyPopover);









  /**
   * =============================================================
   * ## ANNOUNCEMENT BAR
   * =============================================================
   */

  var AnnouncementBar = class extends CustomHTMLElement {
    connectedCallback() {
      if (window.ResizeObserver) {
        this.resizeObserver = new ResizeObserver(this._updateCssVariables.bind(this));
        this.resizeObserver.observe(this);
      }
    }

    _updateCssVariables(entries) {
      entries.forEach((entry) => {
        if (entry.target === this) {
          const height = entry.borderBoxSize ? entry.borderBoxSize.length > 0 ? entry.borderBoxSize[0].blockSize : entry.borderBoxSize.blockSize : entry.target.clientHeight;
          document.documentElement.style.setProperty("--announcement-bar-height", `${height}px`);
        }
      });
    }
  };
  window.customElements.define("announcement-bar", AnnouncementBar);










  /**
   * =============================================================
   * ## HEADER MAIN
   * =============================================================
   */

  var HeaderMain = class extends CustomHTMLElement {
    connectedCallback() {

      /**
       * We use store the header height in a CSS variable. This will update the 
       * CSS variables on window resizes.
       */
      if (window.ResizeObserver) {
        this.resizeObserver = new ResizeObserver(this._updateCssVariables.bind(this));
        this.resizeObserver.observe(this);
      }
    }

    disconnectedCallback() {
      super.disconnectedCallback();
      
      if (window.ResizeObserver) {
        this.resizeObserver.disconnect();
      }
    }

    get isSticky() {
      return this.hasAttribute("sticky");
    }

    _updateCssVariables(entries) {
      entries.forEach((entry) => {
        if (entry.target === this) {
          const height = entry.borderBoxSize ? entry.borderBoxSize.length > 0 ? entry.borderBoxSize[0].blockSize : entry.borderBoxSize.blockSize : entry.target.clientHeight;

          document.documentElement.style.setProperty("--header-height", `${height}px`);
        }
      });
    }
  }

  window.customElements.define("header-main", HeaderMain);










  /**
   * =============================================================
   * ## FASTSIMON PREDICTIVE SEARCH
   * This is powered by the FastSimon app. We have a custom implementation
   * using the SDK: http://docs.fastsimon.com/sdk/docs/introduction-and-installation
   * 
   * The current implementation does the following:
   * - Opens a search results popup on focus of search input
   * - Retrieves popular products when search input is empty
   * - Retrieves product results and popular searches on user input change
   * =============================================================
   */

  var FsPredictiveSearch = class extends CustomHTMLElement {
    static get observedAttributes() {
      return ["open"];
    }

    constructor() {
      super();
      this.originalScrollPadding = "";
      this.isSearchActive = false;
    }

    connectedCallback() {
      this.inputElement = this.querySelector('[name="q"]');
      this.contentElement = this.querySelector(".predictive-search__content");
      this.loadingStateElement = this.querySelector(".predictive-search__loading-state");
      this.noResultsElement = this.querySelector(".predictive-search__no-results");
      this.resultsElement = this.querySelector(".predictive-search__results");
      this.popularSearchesElement = this.querySelector(".predictive-search__popular-searches");
      this.popularProductsElement = this.querySelector(".predictive-search__popular-products");

      // Event handlers
      this.delegate.on("focus", '[name="q"]', this._onFocus.bind(this));
      this.delegate.on("input", '[name="q"]', this._debounce(this._onSearch.bind(this), 200));
      this.delegate.on("blur", '[name="q"]', this._onBlur.bind(this));

      // Event handlers for analytics
      this.delegate.on("click", '.fs-predictive-product', this._onProductClicked.bind(this));
      this.delegate.on("click", '.fs-predictive-popular-search', this._onPopularSearchClicked.bind(this));

      this.previousSearch;
    }

    get searchTerm() {
      return this.inputElement.value;
    }

    get popularProductsLoaded() {
      return this.hasAttribute("popular-products-loaded");
    }

    get open() {
      return this.hasAttribute("open");
    }

    set open(value) {
      if (value) {
        (async () => {
          this.setAttribute("open", "");
        })();
      } else {
        this.removeAttribute("open");
      }
    }

    get shouldTrapFocus() {
      return true;
    }

    get returnFocusOnDeactivate() {
      return false;
    }

    get focusTrap() {
      return this._focusTrap = this._focusTrap || createFocusTrap(this, {
        fallbackFocus: this,
        initialFocus: this.hasAttribute("initial-focus-selector") ? this.getAttribute("initial-focus-selector") : void 0,
        clickOutsideDeactivates: (event) => !(event.target.hasAttribute("aria-controls") && event.target.getAttribute("aria-controls") === this.id),
        allowOutsideClick: (event) => event.target.hasAttribute("aria-controls") && event.target.getAttribute("aria-controls") === this.id,
        returnFocusOnDeactivate: this.returnFocusOnDeactivate,
        onDeactivate: () => this.open = false,
        preventScroll: true
      });
    }

    attributeChangedCallback(name, oldValue, newValue) {
      switch (name) {
        case "open":
          if (oldValue === null && newValue === "") {
            if (this.shouldTrapFocus) {
              setTimeout(() => this.focusTrap.activate(), 150);
            }
            this.inputElement.setAttribute("aria-expanded", "true");
            triggerEvent(this, "openable-element:open");
          } else if (newValue === null) {
            if (this.shouldTrapFocus) {
              this.focusTrap.deactivate();
            }
            this.inputElement.setAttribute("aria-expanded", "false");
            triggerEvent(this, "openable-element:close");
          }
      }
    }

    _onFocus(event, target) {
      // Store original scroll padding
      this.originalScrollPadding = document.documentElement.style.scrollPaddingTop;
      // Temporarily disable scroll padding during search
      document.documentElement.style.scrollPaddingTop = '0';
      this.isSearchActive = true;

      this.setAttribute("open", "");

      if (target.value === "") {
        this._getPopularProducts(event, target);
      } else {
        if (target.value === this.previousSearch) {
          return;
        }
        
        this._onSearch(event, target);
      }
    }

    _onBlur() {
      // Only restore if we're the ones who modified it
      if (this.isSearchActive) {
        document.documentElement.style.scrollPaddingTop = this.originalScrollPadding;
        this.isSearchActive = false;
      }
    }

    async _onSearch(event, target) {
      if (event.key === "Enter") {
        return;
      }

      this.setAttribute("open", "");

      if (this.abortController) {
        this.abortController.abort();
      }

      if (target.value === "") {
        // this.loadingStateElement.hidden = true;
        this.noResultsElement.hidden = true;
        this.resultsElement.hidden = true;
        this.popularSearchesElement.hidden = true;
        this.popularProductsElement.hidden = false;
      } else {
        // this.loadingStateElement.hidden = false;
        this.noResultsElement.hidden = true;
        this.resultsElement.hidden = true;
        this.popularSearchesElement.hidden = true;
        this.popularProductsElement.hidden = true;

        let searchResults = {};

        try {
          this.abortController = new AbortController();
          searchResults = await this._doPredictiveSearch(target.value);
          this.previousSearch = target.value;
        } catch (e) {
          if (e.name === "AbortError") {
            return;
          }
        }

        // this.loadingStateElement.hidden = true;
        this.resultsElement.hidden = false;
        this.popularSearchesElement.hidden = false;
        this.popularProductsElement.hidden = true;
      }
    }

    _truncateByChars(text, maxChars) {
      if (text.length <= maxChars) return text;
      return text.substring(0, maxChars) + '...';
    }

    async _getPopularProducts(event, target) {

      if (target.value === "") {

        this.popularProductsElement.hidden = false;

        if (this.popularProductsLoaded) {
          return;
        }

        window.FastSimonSDK.getAllPopularProducts().then(response => {
          const popularProducts = response;

          if (popularProducts) {
            this.popularProductsElement.innerHTML = "";
            this.popularProductsElement.innerHTML = '<h4 class="predictive-search__section-heading heading heading-h6">Popular Products</h4>';

            for (var i = 0; i < 6; i++) {
              let element = popularProducts[i];
   
              let imageUrl = `${element.t2}`
              let newImageUrl = getSizedMediaUrl(imageUrl, "100x");

              let productPrice = element.p;
              let productPriceFormatted = formatMoney(element.p * 100);

              let popularProduct = `
              <a href="${element.u}" class="predictive-search-item">
                <img class="predictive-search-item__image" src="${newImageUrl}">
                <div class="predictive-search-item__text-wrapper">
                  <h4 class="predictive-search-item__title">${element.l}</h4>
                  <div class="predictive-search-item__text">${this._truncateByChars(element.d, 120)}</div>
                  <p class="predictive-search-item__price"><span class="price--convertible" data-base-amount="${productPrice}">${productPriceFormatted}</span></p>
                </div>
              </a>
              `
              currencyConversionUpdatePrices(this.popularProductsElement);
              this.popularProductsElement.insertAdjacentHTML("beforeend", popularProduct);
            };

            this.setAttribute("popular-products-loaded", "");
          }
        });
      } else {
        this.popularProductsElement.hidden = true;
      }
    }

    async _doPredictiveSearch(term) {
      // Store current scroll position
      const scrollY = window.scrollY;

      this.loadingStateElement.hidden = false;

      window.FastSimonSDK.instantSearch({
        query: term,
        callback: (response) => {
          const productResults = response.payload.products;
          const popularSearches = response.payload.popularSearches;

          if (productResults.length > 0) {
            this.resultsElement.innerHTML = "";

            productResults.forEach((result) => {
              let imageUrl = `${result.t2}`
              let newImageUrl = getSizedMediaUrl(imageUrl, "100x");

              let productPrice = result.p;
              let productPriceFormatted = formatMoney(result.p * 100);

              let productItem = `
              <a href="${result.u}" class="predictive-search-item fs-predictive-product" data-product-id="${result.id}" data-product-query="${result.s}">
                <img class="predictive-search-item__image" src="${newImageUrl}">
                <div class="predictive-search-item__text-wrapper">
                  <h4 class="predictive-search-item__title">${result.l}</h4>
                  <div class="predictive-search-item__text">${this._truncateByChars(result.d, 120)}</div>
                  <p class="predictive-search-item__price"><span class="price--convertible" data-base-amount="${productPrice}">${productPriceFormatted}</span></p>
                </div>
              </a>
              `
              currencyConversionUpdatePrices(this.resultsElement);
              this.resultsElement.insertAdjacentHTML("beforeend", productItem);
            });

            this.loadingStateElement.hidden = true;
            this.noResultsElement.hidden = true;
          } else {
            this.noResultsElement.hidden = false;
            this.loadingStateElement.hidden = true;
            this.resultsElement.hidden = true;
          }

          if (popularSearches.length > 0) {
            this.popularSearchesElement.innerHTML = "";
            this.popularSearchesElement.innerHTML = `
            <h4 class="predictive-search__section-heading heading heading-h6">Popular Searches</h4>`;

            popularSearches.forEach((result) => {

              let popularSearchItem = `
              <a href="/search?q=${result.label}&narrow_by=&sort_by=relevency&page=1" class="predictive-search__popular-search link link--hover fs-predictive-popular-search" data-label="${result.label}">${result.label}
              </a>
              `
              this.popularSearchesElement.insertAdjacentHTML("beforeend", popularSearchItem);
            });
          } else {
            this.popularSearchesElement.hidden = true;
          }
        }
      }); 

      // After DOM updates, restore scroll position if needed
      if (this.isSearchActive) {
        window.scrollTo({ top: scrollY });
      }
    }

    _onProductClicked(event, target) {
      window.FastSimonSDK.event({
        eventName: window.FastSimonEventName.AutocompleteProductClicked,
        data: {
          query: this.searchTerm, // (Required)
          productID: target.getAttribute("data-product-id"), // (Required)
          productQuery: target.getAttribute("data-product-query") // product's 's' key (Required)
        }
      });
    }

    _onPopularSearchClicked(event, target) {
      window.FastSimonSDK.event({
        eventName: window.FastSimonEventName.AutocompletePopularClicked,
        data: {
          query: this.searchTerm, // (Required)
          term: target.getAttribute("data-label") // (Required)
        }
      });
    }

    _debounce(fn, delay3) {
      let timer = null;
      return (...args) => {
        clearTimeout(timer);
        timer = setTimeout(() => {
          fn.apply(this, args);
        }, delay3);
      };
    }
  };

  window.customElements.define("fs-predictive-search", FsPredictiveSearch);











  /**
   * =============================================================
   * ## FS SEARCH PAGE
   * This is powered by the FastSimon app. We have a custom implementation
   * using the SDK: http://docs.fastsimon.com/sdk/docs/introduction-and-installation
   * =============================================================
   */

  var FsSearchPage = class extends CustomHTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.header = this.querySelector(".search-page__header");
      this.noResultsElement = this.querySelector(".search-page__no-results-text");
      this.mobileToolbar = this.querySelector(".mobile-toolbar");

      // Sidebar filter elements
      this.facet = this.querySelector(".product-facet");
      this.sidebar = this.querySelector(".product-facet__aside");
      this.filters = this.querySelector(".product-facet__filters");
      this.filtersForm = this.querySelector(".product-facet__form");
      this.filtersFormHeading = this.querySelector(".product-facet__form-heading");
      this.activeFiltersList = this.querySelector(".product-facet__active-list");
      this.activeFiltersCount = this.querySelector(".product-facet__active-count");
      this.clearFiltersButton = this.querySelector(".product-facet__clear-filters");
      this.filtersList = this.querySelector(".product-facet__filter-list");

      // Main element
      this.main = this.querySelector(".product-facet__main");

      // Meta bar elements
      this.metaBar = this.querySelector(".product-facet__meta-bar");
      this.count = this.querySelector(".product-facet__meta-bar-item--count");
      this.sort = this.querySelector(".product-facet__meta-bar-item--sort");
      this.sortOptions = Array.from(this.querySelectorAll('[name="sort_by_search"]'));

      // Product list elements
      this.productList = this.querySelector(".product-facet__product-list");
      this.results = this.querySelector(".product-list__inner--results");
      this.skeletonResults = this.querySelector(".product-list__inner--skeleton");


      // Pagination elements
      this.pagination = this.querySelector(".pagination");
      this.paginationNav = this.pagination.querySelector(".pagination__nav");

      this.currentUrl = new URL(window.location.href);

      this.currentPage = 1;
      this.pageCount = 1;
      this.totalResults;
      this.narrowByArray = [];

      this.searchParamsInitial = this.currentUrl.searchParams.toString();
      this.searchParamsPrev = this.currentUrl.searchParams.toString();

      this.lastSearch = {
        'searchTerm': '',
        'pageNumber': '',
        'narrowBy': '',
        'sortBy': '',
        'totalResults': 0,
        'filterNames': ''
      };

      if (window.searchLoaded) {
        this._onSearchLoaded();
      }

      this.rootDelegate.on("search:loaded", this._onSearchLoaded.bind(this));
    }

    _initEventListeners() {

      // Event handlers
      this.delegate.on("click", '[data-action="toggle-tag"]', this._onFilterChanged.bind(this));
      this.delegate.on("click", '[data-action="remove-filter"]', this._onRemoveFilter.bind(this));
      this.delegate.on("click", '[data-action="clear-filters"]', this._onFiltersCleared.bind(this));
      this.delegate.on("change", '[name="sort_by_search"]', this._onSortChanged.bind(this));
      this.delegate.on("click", '[data-action="page-change"]', this._onPageChange.bind(this));

      // Analytics
      this.delegate.on("click", '.fs-product-link', this._onProductClicked.bind(this));

      // This handles back button behaviour
      this.rootDelegate.on("history:changed", this._onHistoryChanged.bind(this));

      // These event handlers will place the sidebar in a drawer on small screens
      this.matchMedia = window.matchMedia(window.themeVariables.breakpoints.mediumDown);
      this.matchMedia.addListener(this._adjustDrawer.bind(this));
      this._adjustDrawer(this.matchMedia);
    }

    // This will reinitialise the reviews.io snippet
    _reinitRatingSnippet() {
      reinitialiseRatingSnippet();
    }

    _onSearchLoaded(event, target) {
      this._initEventListeners();
      this._onPageReady();
    }

    get searchTerm() {
      return this.getAttribute("search-term");
    }

    static setListeners() {
      const onHistoryChange = (event) => {
        triggerEvent(document.documentElement, "history:changed", { searchParams: window.location.search.slice(1) });
      }

      window.addEventListener('popstate', onHistoryChange);
    }

    _updateCurrentUrlVariable() {
      this.currentUrl = new URL(window.location.href);
    }

    _onProductClicked(event, target) {
      window.FastSimonSDK.event({
        eventName: window.FastSimonEventName.SerpProductClicked,
        data: {
          query: this.currentUrl.searchParams.get("q"), // (Required)
          productID: target.getAttribute("data-product-id") // (Required)
        }
      }); 
    }

    _onHistoryChanged(event) {
      if (event.detail.searchParams == this.searchParamsPrev) {
        return;
      }

      this._runFromCache(event.detail.searchParams);
    }

    _runFromCache(searchParams) {
      this._updateCurrentUrlVariable();

      var searchTerm = this.currentUrl.searchParams.get("q") || '';
      var pageNumber =  this.currentUrl.searchParams.get("page") || '';
      var narrowBy = this.currentUrl.searchParams.get("narrow_by") || [];
      var sortBy = this.currentUrl.searchParams.get("sort_by") || '';

      if (typeof narrowBy == 'string' && narrowBy !== '') {
        /**
         * The search parameters URL will provide a comma separated string for our filters:
         * `Vendor,Pimoroni,Color,Black`
         * 
         * We need to split this string, and create an array for each filter after
         * every 2 elements.
         */
        var narrowBySplit = narrowBy.split(",");
        var narrowByArrayNew = [];

        if (narrowBySplit.length % 2 == 0) {
          for (var i = 0; i < narrowBySplit.length; i+= 2) {
            var tempArr = [];
            tempArr.push(narrowBySplit[i]);
            tempArr.push(narrowBySplit[i + 1]);
            narrowByArrayNew.push(tempArr);
          }

          narrowBy = narrowByArrayNew;
        }

        this.narrowByArray = narrowByArrayNew;
      }

      this.narrowByArray = narrowBy;

      this._doFullTextSearch(searchTerm, pageNumber, narrowBy, sortBy);
    }

    _onPageReady() {
      var searchTerm = this.currentUrl.searchParams.get("q") || '';
      var pageNumber =  this.currentUrl.searchParams.get("page") || '';
      var narrowBy = this.currentUrl.searchParams.get("narrow_by") || [];
      var sortBy = this.currentUrl.searchParams.get("sort_by") || '';

      if (pageNumber == '') {
        pageNumber = 1;
      }

      /**
       * `narrowBy` filters need to be an array featuring a key object array
       * for each filter. For example: 
       * [["Vendor", "Pimoroni"]["Color", "Black"]]
       */

      if (typeof narrowBy == 'string' && narrowBy !== '') {
        /**
         * The search parameters URL will provide a comma separated string for our filters:
         * `Vendor,Pimoroni,Color,Black`
         * 
         * We need to split this string, and create an array for each filter after
         * every 2 elements.
         */
        var narrowBySplit = narrowBy.split(",");
        var narrowByArrayNew = [];

        if (narrowBySplit.length % 2 == 0) {
          for (var i = 0; i < narrowBySplit.length; i+= 2) {
            var tempArr = [];
            tempArr.push(narrowBySplit[i]);
            tempArr.push(narrowBySplit[i + 1]);
            narrowByArrayNew.push(tempArr);
          }

          narrowBy = narrowByArrayNew;
        }

        this.narrowByArray = narrowByArrayNew;
      }

      this._doFullTextSearch(searchTerm, pageNumber, narrowBy, sortBy);
    }


    _doFullTextSearch(searchTerm, pageNumber, narrowBy, sortBy, scrollIntoView = false) {

      if (searchTerm == "") {
        this._noResultsFound();
      };

      this.skeletonResults.hidden = false;
      this.results.hidden = true;

      if (scrollIntoView) {
        this._scrollIntoView();
      }

      this.count.hidden = true;

      if (sortBy == "" || sortBy == null) {
        sortBy = 'relevency';
      };

      // Update page title with search term
      document.title = searchTerm + " | The Pi Hut";

      // let sessionStorageKey = "search_cache_" + searchTerm + "";

      // Set URL search parameters
      this.currentUrl.searchParams.set("q", searchTerm);
      this.currentUrl.searchParams.set("narrow_by", narrowBy);
      this.currentUrl.searchParams.set("sort_by", sortBy);
      this.currentUrl.searchParams.set("page", pageNumber);

      // Store previous searchParams for back button behaviour
      this.searchParamsPrev = this.currentUrl.searchParams.toString();

      // Store searches in history
      history.pushState({ path: this.currentUrl.toString() }, "", this.currentUrl.toString());

      /**
       * The callback can potentially run twice for `products` and `product and
       * facets`. We use this variable to ensure we don't run the product
       * related callback multiple times.
       */
      let productCallbackHasRun = false;

      window.FastSimonSDK.fullTextSearch({
        term: searchTerm,
        narrowBy: narrowBy,
        sortBy: sortBy,
        facets: true,
        productsPerPage: 24,
        page: pageNumber,
        callback: (response) => {
          const productResults = response.payload.products;
          const facets = response.payload.facets;

          if (productResults.length > 0 && !productCallbackHasRun) {
            productCallbackHasRun = true;
            this.lastSearch.searchTerm = searchTerm;
            this.lastSearch.sortBy = sortBy;

            this.results.innerHTML = "";
            this.skeletonResults.hidden = true;
            this.results.hidden = false;

            productResults.forEach((product) => {
              let productUrl = `${product.u}`;
              let productItemHtml = `
              <div class="product-item product-item--${product.id} product-item--placeholder">
              </div>`;

              this.results.innerHTML += productItemHtml;

              new Promise(async (resolve) => {

                /**
                 * We're using the Shopify Sections API to fetch a template
                 * for each product item.
                 * 
                 * As this process is asynchronous, there is potential for
                 * certain product items to load out of order. We therefore
                 * create a placeholder product item with a unique product id.
                 * These placeholders will be appended in the correct order.
                 * We then find the matching product item from our fetch response
                 * and replace the placeholder as needed.
                 */
                 
                const response = await fetch(`${window.themeVariables.routes.rootUrlWithoutSlash}${productUrl}?view=search-item&section_id=product-search-item`);
                const responseAsText = await response.text();
                const fakeDiv = document.createElement("div");
                fakeDiv.innerHTML = responseAsText;
                const productItem = fakeDiv.querySelector(".product-item--" + product.id);
                const placeholder = this.results.querySelector(".product-item--" + product.id);
                placeholder.replaceWith(productItem);
                currencyConversionUpdatePrices(this.results);
                this._reinitRatingSnippet();
              });
            });

            if (response.payload.isNoResults) {
              this._noResultsFound();
            } else {
              this.count.innerHTML = `${response.payload.totalResults} products for your search`;
              this.count.hidden = false;

              this.sort.hidden = false;
              this.sortOptions.forEach((option) => {
                option.checked = false;

                if (option.value == sortBy) {
                  option.checked = true;
                }
              });

              this._buildPagination(response.payload.totalResults, response.payload.pageCount, response.payload.page);
            }
          }

          if (!response.payload.isNoResults && facets) {
            this.filtersList.innerHTML = "";

            if (narrowBy.length > 0) {
              this.activeFiltersCount.innerHTML = narrowBy.length;
              this.activeFiltersCount.hidden = false;
            } else {
              this.activeFiltersCount.innerHTML = "";
              this.activeFiltersCount.hidden = true;
            }

            facets.forEach((facet, index) => {
              // Hide price range slider
              if (facet[2] == 'Price_min' || facet[2] == 'Price_max') {
                return;
              }

              let facetHtml;

              if (facet[2] == 'Price') {
                facetHtml = `
                <div class="product-facet__filter-item">
                  <button type="button" is="toggle-button" class="collapsible-toggle text--strong" aria-controls="facet-${index}" aria-expanded="false">
                      ${facet[2]}
                    <svg focusable="false" width="12" height="8" class="icon icon--chevron icon--inline" viewBox="0 0 12 8">
                      <path fill="none" d="M1 1l5 5 5-5" stroke="currentColor" stroke-width="1.5"></path>
                    </svg>
                  </button>

                  <collapsible-content animate-items id="facet-${index}" class="collapsible">
                    <div class="collapsible__content">`;


                  let facetValues = facet[1];

                  facetValues.forEach((value) => {
                    let valueLabel = value[0];
                    let parts = valueLabel.split('-');
                    let firstPart = parts[0];
                    let secondPart = parts[1];
                    let firstValue = firstPart.split(":")[1];
                    let firstValueFormatted = formatMoney(firstValue * 100);
                    let secondValue = secondPart.split(":")[1];
                    let secondValueFormatted = formatMoney(secondValue * 100);

                    facetHtml += `
                      <div class="checkbox-container">
                        <input type="checkbox" class="checkbox" id="${index}-tag-${value[0]}" name="tag-${value}" data-action="toggle-tag" data-tag="${value[0]}" data-filter="${facet[2]}">
                        <label for="${index}-tag-${value[0]}">
                          <span class="price--convertible" data-base-amount="${firstValue}">${firstValueFormatted}</span> - <span class="price--convertible" data-base-amount="${secondValue}">${secondValueFormatted}</span>
                          <span class="text--small">(${value[1]})</span>
                        </label>
                      </div>
                    `;
                  });

                facetHtml += `
                    </div>
                  </collapsible-content>
                </div>`;
              } else {

                facetHtml = `
                <div class="product-facet__filter-item">
                  <button type="button" is="toggle-button" class="collapsible-toggle text--strong" aria-controls="facet-${index}" aria-expanded="false">
                      ${facet[2]}
                    <svg focusable="false" width="12" height="8" class="icon icon--chevron icon--inline" viewBox="0 0 12 8">
                      <path fill="none" d="M1 1l5 5 5-5" stroke="currentColor" stroke-width="1.5"></path>
                    </svg>
                  </button>

                  <collapsible-content animate-items id="facet-${index}" class="collapsible">
                    <div class="collapsible__content">`
                    ;

                  let facetValues = facet[1];

                  facetValues.forEach((value) => {
                    let valueLabel;
                    let checkboxContainerClasses;

                    if (facet[2] == 'Categories') {
                      valueLabel = value[2];
                      checkboxContainerClasses = 'category-filter';
                    } else if (value[0].includes('_')) {
                      valueLabel = value[0].split('_')[1];
                    } else {
                      valueLabel = value[0];
                    }

                    /**
                     * The app doesn't recognise if a collection is unpublished
                     * so we can end up with "empty" category filters. This
                     * ensures unpublished collections are hidden.
                     */
                    if (valueLabel) {
                      facetHtml += `
                        <div class="checkbox-container ${checkboxContainerClasses}" data-filter-tag-title="${valueLabel}">
                          <input type="checkbox" class="checkbox" id="${index}-tag-${value[0]}" name="tag-${value}" data-action="toggle-tag" data-tag="${value[0]}"  data-filter="${facet[2]}">
                          <label for="${index}-tag-${value[0]}" style="text-transform: capitalize">
                            <span class="checkbox-label__text">${valueLabel}</span>
                            <span class="text--small">(${value[1]})</span>
                          </label>
                        </div>
                      `;
                    }
                  });

                facetHtml += `
                    </div>
                  </collapsible-content>
                </div>`;
              }

              this.filtersList.innerHTML += facetHtml;
              currencyConversionUpdatePrices(this.filtersList);
            });

            if (narrowBy.length > 0) {
              this.activeFiltersList.innerHTML = "";

              for (var i = 0; i < narrowBy.length; i++) {
                let filterGroup = narrowBy[i][0];
                let activeTag = narrowBy[i][1];
                let activeTagLabel;

                let activeTagElement = this.filtersList.querySelector('[data-tag="' + activeTag + '"]');

                /**
                 * In some instances, we may have a combination of filters wherein
                 * an active filter is not part of the results. Therefore the
                 * `activeTagElement` could be null, hence using a conditional
                 * below to check if it exists.
                 */
                if (activeTagElement) {
                  activeTagElement.classList.add("is-active");
                  activeTagElement.checked = true;
                }

                if (filterGroup == 'Categories') {
                  activeTagLabel = activeTagElement.parentElement.getAttribute("data-filter-tag-title");
                } else if (activeTag.includes('_')) {
                  activeTagLabel = activeTag.split('_')[1];
                } else {
                  activeTagLabel = activeTag;
                }

                let activeFilterTag;

                if (filterGroup == 'Price') {
                  let parts = activeTag.split('-');
                  let firstPart = parts[0];
                  let secondPart = parts[1];
                  let firstValue = firstPart.split(":")[1];
                  let firstValueFormatted = formatMoney(firstValue * 100);
                  let secondValue = secondPart.split(":")[1];
                  let secondValueFormatted = formatMoney(secondValue * 100);

                  activeFilterTag = 
                  `
                  <div class="tag">
                    <button class="tag__icon o-tap-area" data-action="remove-filter" data-tag="${activeTag}">
                      <svg focusable="false" width="10" height="10" class="icon icon--close" viewBox="0 0 14 14">
                        <path d="M13 13L1 1M13 1L1 13" stroke="currentColor" stroke-width="1.5" fill="none"></path>
                      </svg>
                    </button>
                    <span class="price--convertible" data-base-amount="${firstValue}">${firstValueFormatted}</span> - <span class="price--convertible" data-base-amount="${secondValue}">${secondValueFormatted}</span>
                  </div>`;
                } else {

                  activeFilterTag = 
                  `
                  <div class="tag">
                    <button class="tag__icon o-tap-area" data-action="remove-filter" data-tag="${activeTag}" data-tag-title="${activeTag}">
                      <svg focusable="false" width="10" height="10" class="icon icon--close" viewBox="0 0 14 14">
                        <path d="M13 13L1 1M13 1L1 13" stroke="currentColor" stroke-width="1.5" fill="none"></path>
                      </svg>
                    </button>
                    ${activeTagLabel}
                  </div>`;
                }

                this.activeFiltersList.innerHTML += activeFilterTag;
                currencyConversionUpdatePrices(this.activeFiltersList);
              }

              this.filtersFormHeading.hidden = false;
              this.activeFiltersList.hidden = false;
              this.clearFiltersButton.hidden = false;
            } else {
              this.filtersFormHeading.hidden = false;
              this.activeFiltersList.innerHTML = "";
              this.activeFiltersList.hidden = true;
              this.clearFiltersButton.hidden = true;
            }

            this._endSidebarLoading();
          }
        }
      });

      window.FastSimonSDK.event({
        eventName: window.FastSimonEventName.SearchPerformed,
        data: {
          query: searchTerm,
          narrowBy: narrowBy,
          sortBy: sortBy
        }
      });
    }

    _noResultsFound() {
      this.sidebar.hidden = true;
      this.metaBar.hidden = true;
      this.mobileToolbar.hidden = true;
      this.noResultsElement.hidden = false;
      document.title = "Search | The Pi Hut";
    }

    _buildPagination(totalResults, pageCount, currentPage) {
      /**
       * We have a slightly complex pagination pattern that will truncate the
       * amount of buttons shown. The inspiration for the code is here:
       * https://stackoverflow.com/questions/56670877/how-to-build-a-pagination-with-ellipsis/56671681#56671681
       */

      if (pageCount < 2) {
        this.pagination.hidden = true;
        return;
      }

      let navInnerHtml = '';

      if (currentPage > 1) {
        navInnerHtml += `
          <a class="pagination__nav-item" rel="prev" aria-label="previous" data-page="${currentPage - 1}" data-action="page-change">
            <svg focusable="false" width="17" height="14" class="icon icon--nav-arrow-left" viewBox="0 0 17 14">
              <path d="M17 7H2M8 1L2 7l6 6" stroke="currentColor" stroke-width="1.5" fill="none"></path>
            </svg>
          </a>`
      }

      let outOfRange = false;

      for (var i = 1; i <= pageCount; i++) {
        if (i <= 1 || i > pageCount - 1 || Math.abs(i - currentPage) <= 1) {
          outOfRange = false;

          if (i == currentPage) {
            navInnerHtml += `<span class="pagination__nav-item" aria-current="page">${i}</span>`
          } else {
            navInnerHtml += `
            <a class="pagination__nav-item" data-page="${i}" data-action="page-change" aria-label="Navigate to page ${i}">${i}</a>`
          }
        } else {
          if (!outOfRange) {
            navInnerHtml += `<span class="pagination__nav-item">...</span>`;
          }

          outOfRange = true;
        }
      }

      if (currentPage < pageCount) {
        navInnerHtml += `
          <a class="pagination__nav-item" rel="next" aria-label="next" data-page="${currentPage + 1}" data-action="page-change">
            <svg focusable="false" width="17" height="14" class="icon icon--nav-arrow-right" viewBox="0 0 17 14">
              <path d="M0 7h15M9 1l6 6-6 6" stroke="currentColor" stroke-width="1.5" fill="none"></path>
            </svg>
          </a>`
      }

      this.paginationNav.innerHTML = navInnerHtml;
      this.pagination.hidden = false;
    }


    _onSortChanged(event, target) {
      event.preventDefault();
      let sortBy = target.value;
      this.sort.querySelector(".popover-button").setAttribute("aria-expanded", "false");
      this.sort.querySelector(".popover-x").open = false;

      this._doFullTextSearch(this.lastSearch.searchTerm, 1, this.narrowByArray, sortBy, true);
    };


    _onPageChange(event, target) {
      event.preventDefault();
      let page = target.getAttribute("data-page");
      this._doFullTextSearch(this.lastSearch.searchTerm, page, this.narrowByArray, this.lastSearch.sortBy, true);
    }


    _onRemoveFilter(event, target) {
      this._startSidebarLoading();
      event.preventDefault();

      var filterTag = target.getAttribute('data-tag');
      var filterArray = [];

      if (this.narrowByArray.length < 2) {
        this.narrowByArray = [];
      } else {
        for (var i = 0; i < this.narrowByArray.length; i++) {
          if (this.narrowByArray[i][1] == filterTag) {
            this.narrowByArray.splice(i, 1);
          }
        }
      }

      this._doFullTextSearch(this.lastSearch.searchTerm, 1, this.narrowByArray, this.lastSearch.sortBy, true);
    };


    _onFilterChanged(event, target) {
      this._startSidebarLoading();
      event.preventDefault();

      var filterGroup = target.getAttribute('data-filter');
      var filterTag = target.getAttribute('data-tag');
      var filterArray = [];

      if (target.classList.contains("is-active")) {
        // If no other filters
        if (this.narrowByArray.length < 2) {
          this.narrowByArray = [];
        } else {
          for (var i = 0; i < this.narrowByArray.length; i++) {
            if (this.narrowByArray[i][1] == filterTag) {
              this.narrowByArray.splice(i, 1);
            }
          }
        }

        this._doFullTextSearch(this.lastSearch.searchTerm, 1, this.narrowByArray, this.lastSearch.sortBy, true);
      } else {
        filterArray.push(filterGroup);
        filterArray.push(filterTag);
        this.narrowByArray.push(filterArray);
        target.checked = true;
        this._doFullTextSearch(this.lastSearch.searchTerm, 1, this.narrowByArray, this.lastSearch.sortBy, true);
      }
    };


    _onFiltersCleared(event, target) {
      event.preventDefault();
      this._startSidebarLoading();

      this.narrowByArray = [];
      this._doFullTextSearch(this.lastSearch.searchTerm, 1, this.narrowByArray, this.lastSearch.sortBy, true);
    };


    _scrollIntoView() {
      const scrollTo = this.querySelector(".product-facet__meta-bar") || this.querySelector(".product-facet__product-list") || this.querySelector(".product-facet__main");
      requestAnimationFrame(() => {
        scrollTo.scrollIntoView({ block: "start", behavior: "auto" });
      });
    }

    _startSidebarLoading() {
      this.sidebar.classList.add("sidebar--is-loading");
    }

    _endSidebarLoading() {
      this.sidebar.classList.remove("sidebar--is-loading");
    }

    _adjustDrawer(match) {
      this.filters.classList.toggle("drawer", match.matches);
      this.filters.classList.toggle("drawer--from-left", match.matches);
    }
  };

  window.customElements.define("fs-search-page", FsSearchPage);
  FsSearchPage.setListeners();










  /**
   * =============================================================
   * ## FS SEARCH FACET FILTERS
   *    This simply allows us to extend our Drawer component for the Fast Simon
   *    search integration
   * =============================================================
   */

  var FsSearchFacetFilters = class extends DrawerContent {
    connectedCallback() {
      super.connectedCallback();
    }
  };

  window.customElements.define("fs-search-facet-filters", FsSearchFacetFilters);







  /**
   * =============================================================
   * ## DESKTOP NAVIGATION
   * =============================================================
   */

  var DesktopNavigation = class extends CustomHTMLElement {
    connectedCallback() {
      this.openingTimeout = null;
      this.currentMegaMenu = null;

      this.delegate.on("mouseenter", ".has-dropdown", (event, target) => {
        if (event.target === target && event.relatedTarget !== null) {
          this.openDropdown(target);
        }
      }, true);

      /**
       * Click event handlers for touch screens 
       */
      this.delegate.on("click", ".header__linklist-link[aria-expanded], .nav-dropdown__link[aria-expanded]", (event, target) => {
        if (window.matchMedia("(hover: hover)").matches || target.getAttribute("aria-expanded") === "true") {
          return;
        }
        event.preventDefault();
        this.openDropdown(target.parentElement);
      });

      /**
       * Shopify theme editor events
       */
      this.delegate.on("shopify:block:select", (event) => this.openDropdown(event.target.parentElement));
      this.delegate.on("shopify:block:deselect", (event) => this.closeDropdown(event.target.parentElement));
    }

    openDropdown(parentElement) {
      const menuItem = parentElement.querySelector("[aria-controls]");
      const dropdown = parentElement.querySelector(`#${menuItem.getAttribute("aria-controls")}`);

      // Get current mega menu or set to null if it's a standard nav dropdown
      this.currentMegaMenu = dropdown.classList.contains("mega-menu") ? dropdown : null;

      let openingTimeout = setTimeout(() => {
        // If already open, then return
        if (menuItem.getAttribute("aria-expanded") === "true") {
          return;
        }

        // Set attributes for accessibility and CSS
        menuItem.setAttribute("aria-expanded", "true");
        dropdown.removeAttribute("hidden");

        const leaveListener = (event) => {
          if (event.relatedTarget !== null) {
            this.closeDropdown(parentElement);
            parentElement.removeEventListener("mouseleave", leaveListener);
          }
        };

        parentElement.addEventListener("mouseleave", leaveListener);
        openingTimeout = null;

        this.dispatchEvent(new CustomEvent("desktop-nav:dropdown:open", { bubbles: true }));
      }, 100);

      parentElement.addEventListener("mouseleave", () => {
        if (openingTimeout) {
          clearTimeout(openingTimeout);
        }
      }, { once: true });
    }

    closeDropdown(parentElement) {
      const menuItem = parentElement.querySelector("[aria-controls]");
      const dropdown = parentElement.querySelector(`#${menuItem.getAttribute("aria-controls")}`);

      requestAnimationFrame(() => {

        // Set attributes for CSS and accessibility
        menuItem.setAttribute("aria-expanded", "false");

        setTimeout(() => {
          dropdown.setAttribute("hidden", "");
          clearTimeout(this.openingTimeout);
        }, dropdown.classList.contains("mega-menu") && this.currentMegaMenu !== dropdown ? 250 : 0);

        this.dispatchEvent(new CustomEvent("desktop-nav:dropdown:close", { bubbles: true }));
      });
    }
  };

  window.customElements.define("desktop-navigation", DesktopNavigation);










  /**
   * =============================================================
   * ## MOBILE NAVIGATION
   * =============================================================
   */

  var MobileNavigation = class extends DrawerContent {
    connectedCallback() {
      super.connectedCallback();

      this.querySelectorAll('summary').forEach(summary => summary.addEventListener('click', this.onSummaryClick.bind(this)));

      this.querySelectorAll('.mobile-nav__submenu-close-button').forEach(button => button.addEventListener('click', this.onCloseButtonClick.bind(this)));

      document.addEventListener("openable-element:close", (event) => {
        if (event.target == this) {
          this.querySelector(".mobile-nav").classList.remove("submenu-2-is-open");

          this.querySelectorAll('details').forEach(details =>  {
            details.removeAttribute('open');
            details.classList.remove('sub-menu-opening');
            details.querySelector('summary').setAttribute('aria-expanded', false);
          });

          event.stopPropagation();
        }
      });
    }

    attributeChangedCallback(name, oldValue, newValue) {
      super.attributeChangedCallback(name, oldValue, newValue);
      switch (name) {
        case "open":
          triggerEvent(this, this.open ? "mobile-nav:open" : "mobile-nav:close");
      }
    }

    onSummaryClick(event) {
      const summaryElement = event.currentTarget;
      const detailsElement = summaryElement.parentNode;
      const isOpen = detailsElement.hasAttribute('open');
      const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

      if (summaryElement.classList.contains("mobile-nav__link--summary--2")) {
        this.querySelector(".mobile-nav").classList.add("submenu-2-is-open");
        this.querySelector(".drawer__content").scrollTop = 0;
      } else {
        this.querySelector(".mobile-nav").classList.remove("submenu-2-is-open");
      }

      setTimeout(() => {
        detailsElement.classList.add('sub-menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
      }, 100);
    }

    onCloseButtonClick(event) {
      this.querySelector(".mobile-nav").classList.remove("submenu-2-is-open");
      const detailsElement = event.currentTarget.closest('details');
      this.closeSubmenu(detailsElement);
    }

    closeSubmenu(detailsElement) {
      this.querySelector(".mobile-nav").classList.remove("submenu-2-is-open");
      detailsElement.classList.remove('sub-menu-opening');
      detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
      this.closeAnimation(detailsElement);
    }

    closeAnimation(detailsElement) {
      let animationStart;

      const handleAnimation = (time) => {
        if (animationStart === undefined) {
          animationStart = time;
        }

        const elapsedTime = time - animationStart;

        if (elapsedTime < 400) {
          window.requestAnimationFrame(handleAnimation);
        } else {
          detailsElement.removeAttribute('open');
        }
      }

      window.requestAnimationFrame(handleAnimation);
    }
  };

  window.customElements.define("mobile-navigation", MobileNavigation);





















  /**
   * =============================================================
   * ## EXTERNAL VIDEO
   * =============================================================
   */

  var ExternalVideo = class extends CustomHTMLElement {
    constructor() {
      super();

      this.hasLoaded = false;

      (async () => {
        if (this.autoPlay) {
          // await this.untilVisible({ rootMargin: "300px", threshold: 0 });
          this.play();
        } else {
          this.addEventListener("click", this.play.bind(this), { once: true });
        }
      })();
    }

    get autoPlay() {
      return this.hasAttribute("autoplay");
    }

    get provider() {
      return this.getAttribute("provider");
    }

    async play() {
      if (!this.hasLoaded) {
        await this._setupPlayer();
      }

      if (this.provider === "youtube") {
        this.querySelector("iframe").contentWindow.postMessage(JSON.stringify({ event: "command", func: "playVideo", args: "" }), "*");
      } else if (this.provider === "vimeo") {
        this.querySelector("iframe").contentWindow.postMessage(JSON.stringify({ method: "play" }), "*");
      }
    }

    pause() {
      if (!this.hasLoaded) {
        return;
      }

      if (this.provider === "youtube") {
        this.querySelector("iframe").contentWindow.postMessage(JSON.stringify({ event: "command", func: "pauseVideo", args: "" }), "*");
      } else if (this.provider === "vimeo") {
        this.querySelector("iframe").contentWindow.postMessage(JSON.stringify({ method: "pause" }), "*");
      }
    }

    _setupPlayer() {
      if (this._setupPromise) {
        return this._setupPromise;
      }

      return this._setupPromise = new Promise((resolve) => {
        const template2 = this.querySelector("template");
        const node = template2.content.firstElementChild.cloneNode(true);

        node.onload = () => {
          this.hasLoaded = true;
          resolve();
        };

        if (this.autoPlay) {
          template2.replaceWith(node);
        } else {
          this.innerHTML = "";
          this.appendChild(node);
        }
      });
    }
  };
  window.customElements.define("external-video", ExternalVideo);










  /**
   * =============================================================
   * ## NATIVE VIDEO
   * =============================================================
   */

  var NativeVideo = class extends HTMLElement {
    constructor() {
      super();

      this.hasLoaded = false;

      if (this.autoPlay) {
        this.play();
      } else {
        this.addEventListener("click", this.play.bind(this), { once: true });
      }
    }

    get autoPlay() {
      return this.hasAttribute("autoplay");
    }

    play() {
      if (!this.hasLoaded) {
        this._replaceContent();
      }

      this.querySelector("video").play();
    }

    pause() {
      if (this.hasLoaded) {
        this.querySelector("video").pause();
      }
    }

    _replaceContent() {
      const node = this.querySelector("template").content.firstElementChild.cloneNode(true);
      this.innerHTML = "";
      this.appendChild(node);

      this.firstElementChild.addEventListener("play", () => {
        this.dispatchEvent(new CustomEvent("video:played", { bubbles: true }));
      });

      this.firstElementChild.addEventListener("pause", () => {
        this.dispatchEvent(new CustomEvent("video:paused", { bubbles: true }));
      });

      this.hasLoaded = true;
    }
  };

  window.customElements.define("native-video", NativeVideo);











  /**
   * =============================================================
   * ## PRODUCT CAROUSEL 
   * =============================================================
   */

  var ProductCarousel = class extends CustomHTMLElement {
    connectedCallback() {
      this.items = Array.from(this.querySelectorAll(".product__media-item"));

      if (this.items < 2) {
        return;
      }

      this.initPages();

      if (window.ResizeObserver) {
        // Resize event listener on the carousel
        const resizeObserver = new ResizeObserver(entries => {
          this.initPages();
        });

        resizeObserver.observe(this);
      }

      // A scroll event listener
      this.addEventListener("scroll", this.update.bind(this));
    }

    disconnectedCallback() {
      super.disconnectedCallback();
    }

    initPages() {
      // Defensive code to ensure slideshow items are larger than 0 width
      this.itemsToShow = Array.from(this.items).filter(element => element.clientWidth > 0);

      if (this.itemsToShow.length < 2) {
        return;
      }

      this.itemOffset = this.itemsToShow[1].offsetLeft - this.itemsToShow[0].offsetLeft;

      this.itemsPerPage = Math.floor((this.clientWidth - this.itemsToShow[0].offsetLeft) / this.itemOffset);

      this.totalPages = this.itemsToShow.length - this.itemsPerPage + 1;

      this.update();
    }

    update() {
      const previousPage = this.currentPage;

      // Get currentPage
      this.currentPage = Math.round(this.scrollLeft / this.itemOffset) + 1;

      // Checks for slide change and triggers event
      if (this.currentPage != previousPage) {
        triggerEvent(this, "product-carousel:slide-changed", { 
          currentPage: this.currentPage,
          selectedElement: this.itemsToShow[this.currentPage - 1]
        });
      }
    }

    resetPages() {
      this.items = this.querySelectorAll(".product__media-item");
      this.initPages();
    }
  };

  window.customElements.define("product-carousel", ProductCarousel);










  /**
   * =============================================================
   * ## PRODUCT CAROUSEL CONTROLS 
   * =============================================================
   */

  var ProductCarouselControls = class extends HTMLElement {
    connectedCallback() {}
  };

  window.customElements.define("product-carousel-controls", ProductCarouselControls);










  /**
   * =============================================================
   * ## PRODUCT MEDIA 
   * =============================================================
   */

  var ProductMedia = class extends CustomHTMLElement {
    connectedCallback() {
      var _a;

      this.productCarousel = this.querySelector("product-carousel");
      this.productThumbnails = this.querySelector("product-carousel-controls");

      /**
       * Click event listener for product thumbnails which will pass in the 
       * data-media-id 
       */
      if (this.productThumbnails) {
        this.productThumbnails.querySelectorAll('[data-action="select"]').forEach((mediaToSwitch) => {
          mediaToSwitch.addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.mediaId, false));
        });
      }

      if (this.productCarousel.childElementCount === 1) {
        return;
      }

      this.viewInSpaceElement = this.querySelector("[data-shopify-model3d-id]");

      this.selectedVariantMediaId = null;
      this.selectedElement = this.productCarousel.querySelector(".is-selected");

      // Check if product form with related form-id exists so we can listen for variant changes
      var form = document.getElementById(this.getAttribute("form-id"));
      (_a = document.getElementById(this.getAttribute("form-id"))) == null ? void 0 : _a.addEventListener("variant:changed", this._onVariantChanged.bind(this));

      this.productCarousel.addEventListener("product-carousel:slide-changed", debounce(this._onSlideChanged.bind(this), 500));

      this._onReady();
    }

    get thumbnailsPosition() {
      // If medium-down screen, then set thumbnails position as bottom, otherwise use section settings
      return window.matchMedia(window.themeVariables.breakpoints.mediumDown).matches ? "bottom" : this.getAttribute("thumbnails-position");
    }

    _onReady() {
      // Play autoplay video if it's the selected element
      if (["video", "external_video"].includes(this.selectedElement.getAttribute("data-media-type")) && this.hasAttribute("autoplay-video")) {
        this.selectedElement.firstElementChild.play();
      }
    }

    _onSlideChanged(event) {
      const mediaId = event.detail.selectedElement.dataset.mediaId;
      const activeMedia = this.productCarousel.querySelector(`[data-media-id="${ mediaId }"]`);
      const activeThumbnail = this.productThumbnails.querySelector(`[data-media-id="${mediaId}"]`);
      this.setActiveThumbnail(activeThumbnail);

      if (activeMedia.getAttribute("data-media-type") != "image") {
        this.checkMedia(activeMedia);
      }
    }

    setActiveThumbnail(activeThumbnail, animate = true) {
      if (!this.productThumbnails) {
        return;
      }

      this.productThumbnails.querySelectorAll('.product__thumbnail-item').forEach((element) => {
        element.removeAttribute("aria-current");
      });

      // Run scroll animation on thumbnails. This scrolls to the active thumbnail
      requestAnimationFrame(() => {
        if (activeThumbnail.offsetParent && activeThumbnail.offsetParent !== this) {
          const windowHalfHeight = activeThumbnail.offsetParent.clientHeight / 2, windowHalfWidth = activeThumbnail.offsetParent.clientWidth / 2;

          activeThumbnail.offsetParent.scrollTo({
            behavior: animate ? "smooth" : "auto",
            top: activeThumbnail.offsetTop - windowHalfHeight + activeThumbnail.clientHeight / 2,
            left: activeThumbnail.offsetLeft - windowHalfWidth + activeThumbnail.clientWidth / 2
          });
        }
      });

      activeThumbnail.setAttribute("aria-current", true);
    }


    setActiveMedia(mediaId, prepend, animate = true) {
      const activeMedia = this.productCarousel.querySelector(`[data-media-id="${ mediaId }"]`);

      // Pause any existing media and remove active class
      this.querySelectorAll('[data-media-id]').forEach((element) => {
        if (["external_video", "video", "model"].includes(element.getAttribute("data-media-type"))) {
          element.firstElementChild.pause();
        }

        element.classList.remove("is-selected");
      });

      // Add active class
      activeMedia.classList.add("is-selected");

      // On variant selection we prepend the image and thumbnail.
      // This is a recommended practice on the new Online Store 2.0 themese from
      // Shopify.
      if (prepend) {
        activeMedia.parentElement.prepend(activeMedia);

        if (this.productThumbnails) {
          const activeThumbnail = this.productThumbnails.querySelector(`[data-media-id="${ mediaId }"]`);
          activeThumbnail.parentElement.prepend(activeThumbnail);
        }

        if (this.productCarousel) {
          this.productCarousel.resetPages();
        }
      }

      // Scrolls to the active media
      window.setTimeout(() => {
        activeMedia.parentElement.scrollTo({
          left: activeMedia.offsetLeft
        });
      });

      if (activeMedia.getAttribute("data-media-type") != "image") {
        this.checkMedia(activeMedia);
      }

      if (!this.productThumbnails) {
        return;
      }
      
      const activeThumbnail = this.productThumbnails.querySelector(`[data-media-id="${ mediaId }"]`);
      this.setActiveThumbnail(activeThumbnail);
    }

    checkMedia(activeMedia) {
      if (this.viewInSpaceElement) {
        this.viewInSpaceElement.setAttribute("data-shopify-model3d-id", this.viewInSpaceElement.getAttribute("data-shopify-model3d-default-id"));
      }

      // Play media if selected
      switch (activeMedia.getAttribute("data-media-type")) {
        case "model":
          this.viewInSpaceElement.setAttribute("data-shopify-model3d-id", activeMedia.getAttribute("data-media-id"));
          activeMedia.firstElementChild.play();
          break;
        case "external_video":
          if (this.hasAttribute("autoplay-video")) {
            activeMedia.firstElementChild.play();
          }
        case "video":
          if (this.hasAttribute("autoplay-video")) {
            activeMedia.firstElementChild.play();
          }
          break;
      }
    }

    _onVariantChanged(event) {
      const mediaId = event.detail.variant.featured_media.id;
      this.setActiveMedia(mediaId, true);
    }
  };

  window.customElements.define("product-media", ProductMedia);










  /**
   * =============================================================
   * ## PRODUCT ZOOM MODAL
   * =============================================================
   */

  var ProductZoomModal = class extends ModalContent {
    connectedCallback() {
      super.connectedCallback();
    }

    setActiveMedia(mediaId) {
      const activeMedia = this.querySelector(`[data-media-id="${ mediaId }"]`);

      this.querySelectorAll('[data-media-id]').forEach((element) => {
        element.classList.remove("is-selected");
      });

      activeMedia.classList.add("is-selected");
    }
  }

  window.customElements.define("product-zoom-modal", ProductZoomModal);










  /**
   * =============================================================
   * ## PRODUCT META
   * =============================================================
   */

  var ProductMeta = class extends HTMLElement {
    connectedCallback() {

      var _a;

      (_a = document.getElementById(this.getAttribute("form-id"))) == null ? void 0 : _a.addEventListener("variant:changed", this._onVariantChanged.bind(this));
    }

    _onVariantChanged(event) {
      this._updateSku(event.detail.variant);
    }

    _updateSku(variant) {
      let productSku = this.querySelector("[data-product-sku-container]");

      if (!productSku) {
        return;
      }

      let productSkuNumber = productSku.querySelector("[data-product-sku-number]");
      if (!variant || !variant["sku"]) {
        productSku.style.display = "none";
      } else {
        productSkuNumber.innerHTML = variant["sku"];
        productSku.style.display = "";
      }
    }
  };

  window.customElements.define("product-meta", ProductMeta);










  /**
   * =============================================================
   * ## PRODUCT VARIANTS
   * =============================================================
   */

  var ProductVariants = class extends CustomHTMLElement {

    async connectedCallback() {
      this.masterSelector = document.getElementById(this.getAttribute("form-id")).id;
      this.optionSelectors = Array.from(this.querySelectorAll("[data-selector-type]"));

      if (!this.masterSelector) {
        console.warn(`The variant selector for product with handle ${this.productHandle} is not linked to any product form.`);
        return;
      }

      this.product = await ProductLoader.load(this.productHandle);

      this.delegate.on("change", '[name^="option"]', this._onOptionChanged.bind(this));
      this.masterSelector.addEventListener("change", this._onMasterSelectorChanged.bind(this));

      this._updateDisableSelectors();
      this.selectVariant(this.selectedVariant["id"]);
    }

    get selectedVariant() {
      return this._getVariantById(parseInt(this.masterSelector.value));
    }

    get productHandle() {
      return this.getAttribute("handle");
    }

    get hideSoldOutVariants() {
      return this.hasAttribute("hide-sold-out-variants");
    }

    get updateUrl() {
      return this.hasAttribute("update-url");
    }

    selectVariant(id) {
      var _a;

      if (!this._isVariantSelectable(this._getVariantById(id))) {
        id = this._getFirstMatchingAvailableVariant()["id"];
      }

      if (((_a = this.selectedVariant) == null ? void 0 : _a.id) === id) {
        return;
      }

      this.masterSelector.value = id;

      this.masterSelector.dispatchEvent(new Event("change", { bubbles: true }));

      if (this.updateUrl && history.replaceState) {
        const newUrl = new URL(window.location.href);
        if (id) {
          newUrl.searchParams.set("variant", id);
        } else {
          newUrl.searchParams.delete("variant");
        }
        window.history.replaceState({ path: newUrl.toString() }, "", newUrl.toString());
      }

      this._updateDisableSelectors();

      triggerEvent(this.masterSelector.form, "variant:changed", { variant: this.selectedVariant });
    }

    _onOptionChanged() {
      var _a;
      this.selectVariant((_a = this._getVariantFromOptions()) == null ? void 0 : _a.id);
    }

    _onMasterSelectorChanged() {
      var _a;

      const options = ((_a = this.selectedVariant) == null ? void 0 : _a.options) || [];

      options.forEach((value, index) => {
        let input = this.querySelector(`input[name="option${index + 1}"][value="${CSS.escape(value)}"], select[name="option${index + 1}"]`); 
        let triggerChangeEvent = false;

        if (input.tagName === "SELECT") {
          triggerChangeEvent = input.value !== value;
          input.value = value;
        } else if (input.tagName === "INPUT") {
          triggerChangeEvent = !input.checked && input.value === value;
          input.checked = input.value === value;
        }

        if (triggerChangeEvent) {
          input.dispatchEvent(new Event("change", { bubbles: true }));
        }
      });
    }

    _getVariantById(id) {
      return this.product["variants"].find((variant) => variant["id"] === id);
    }

    _getVariantFromOptions() {
      const options = this._getSelectedOptionValues();

      return this.product["variants"].find((variant) => {
        return variant["options"].every((value, index) => value === options[index]);
      });
    }

    _isVariantSelectable(variant) {
      if (!variant) {
        return false;
      } else {
        return variant["available"] || !this.hideSoldOutVariants && !variant["available"];
      }
    }

    _getFirstMatchingAvailableVariant() {
      let options = this._getSelectedOptionValues();
      let matchedVariant = null; 
      let slicedCount = 0;

      do {
        options.pop();
        slicedCount += 1;
        matchedVariant = this.product["variants"].find((variant) => {
          return variant["available"] && variant["options"].slice(0, variant["options"].length - slicedCount).every((value, index) => value === options[index]);
        });
      } while (!matchedVariant && options.length > 0);

      return matchedVariant;
    }

    _getSelectedOptionValues() {
      const options = [];

      Array.from(this.querySelectorAll('input[name^="option"]:checked, select[name^="option"]')).forEach((option) => options.push(option.value));
      return options;
    }

    _updateDisableSelectors() {
      const selectedVariant = this.selectedVariant;

      if (!selectedVariant) {
        return;
      }

      const applyClassToSelector = (selector, valueIndex, available, hasAtLeastOneCombination) => {
        let selectorType = selector.getAttribute("data-selector-type"), cssSelector = "";

        switch (selectorType) {
          case "color":
            cssSelector = `.color-swatch:nth-child(${valueIndex + 1})`;
            break;
          case "variant-image":
            cssSelector = `.variant-swatch:nth-child(${valueIndex + 1})`;
            break;
          case "block":
            cssSelector = `.block-swatch:nth-child(${valueIndex + 1})`;
            break;
          case "dropdown":
            cssSelector = `.combo-box__option-item:nth-child(${valueIndex + 1})`;
            break;
        }

        selector.querySelector(cssSelector).toggleAttribute("hidden", !hasAtLeastOneCombination);

        if (this.hideSoldOutVariants) {
          selector.querySelector(cssSelector).toggleAttribute("hidden", !available);
        } else {
          selector.querySelector(cssSelector).classList.toggle("is-disabled", !available);
        }
      };

      if (this.optionSelectors && this.optionSelectors[0]) {
        this.product["options"][0]["values"].forEach((value, valueIndex) => {

          const hasAtLeastOneCombination = this.product["variants"].some((variant) => variant["option1"] === value && variant);
          const hasAvailableVariant = this.product["variants"].some((variant) => variant["option1"] === value && variant["available"]);

          applyClassToSelector(this.optionSelectors[0], valueIndex, hasAvailableVariant, hasAtLeastOneCombination);

          if (this.optionSelectors[1]) {
            this.product["options"][1]["values"].forEach((value2, valueIndex2) => {
              const hasAtLeastOneCombination2 = this.product["variants"].some((variant) => variant["option2"] === value2 && variant["option1"] === selectedVariant["option1"] && variant);

              const hasAvailableVariant2 = this.product["variants"].some((variant) => variant["option2"] === value2 && variant["option1"] === selectedVariant["option1"] && variant["available"]);

              applyClassToSelector(this.optionSelectors[1], valueIndex2, hasAvailableVariant2, hasAtLeastOneCombination2);

              if (this.optionSelectors[2]) {
                this.product["options"][2]["values"].forEach((value3, valueIndex3) => {
                  const hasAtLeastOneCombination3 = this.product["variants"].some((variant) => variant["option3"] === value3 && variant["option1"] === selectedVariant["option1"] && variant["option2"] === selectedVariant["option2"] && variant);
                  
                  const hasAvailableVariant3 = this.product["variants"].some((variant) => variant["option3"] === value3 && variant["option1"] === selectedVariant["option1"] && variant["option2"] === selectedVariant["option2"] && variant["available"]);

                  applyClassToSelector(this.optionSelectors[2], valueIndex3, hasAvailableVariant3, hasAtLeastOneCombination3);
                });
              }
            });
          }
        });
      }
    }
  };

  window.customElements.define("product-variants", ProductVariants);










  /**
   * =============================================================
   * ## PRODUCT INVENTORY
   * =============================================================
   */

  var ProductInventory = class extends HTMLElement {
    connectedCallback() {
      var _a;
      const scriptTag = this.querySelector("script");
      if (!scriptTag) {
        return;
      }

      this.inventories = JSON.parse(scriptTag.innerHTML);

      (_a = document.getElementById(this.getAttribute("form-id"))) == null ? void 0 : _a.addEventListener("variant:changed", this._onVariantChanged.bind(this));
    }

    _onVariantChanged(event) {
      var _a;

      (_a = this.querySelector("span")) == null ? void 0 : _a.remove();
      if (event.detail.variant && this.inventories[event.detail.variant["id"]] !== "") {
        this.hidden = false;
        this.insertAdjacentHTML("afterbegin", this.inventories[event.detail.variant["id"]]);
      } else {
        this.hidden = true;
      }
    }
  };

  window.customElements.define("product-inventory", ProductInventory);











  /**
   * =============================================================
   * ## PRODUCT PRICE
   * =============================================================
   */

  var ProductPrice = class extends HTMLElement {
    connectedCallback() {
      var _a;
      (_a = document.getElementById(this.getAttribute("form-id"))) == null ? void 0 : _a.addEventListener("variant:changed", this._onVariantChanged.bind(this));
    }

    get priceClass() {
      return this.getAttribute("price-class") || "";
    }

    get unitPriceClass() {
      return this.getAttribute("unit-price-class") || "";
    }

    _onVariantChanged(event) {
      this._updatePrices(event.detail.variant);
      this._updateLabels(event.detail.variant);
    }

    _updatePrices(variant) {
      let productPrices = this.querySelector("[data-product-price-list]");
      // let currencyFormat = window.themeVariables.settings.currencyCodeEnabled ? window.themeVariables.settings.moneyWithCurrencyFormat : window.themeVariables.settings.moneyFormat;
      let currencyFormat = '£ {{ amount_no_decimals }}'

      let noCurrencyFormat = '{{ amount }}';


      if (!productPrices) {
        return;
      }

      if (!variant) {
        productPrices.style.display = "none";
      } else {
        productPrices.setAttribute("data-taxable", variant.taxable);
        productPrices.innerHTML = "";

        if (variant["compare_at_price"] > variant["price"]) {
          productPrices.innerHTML += `<span class="price price--highlight ${this.priceClass}"><span class="visually-hidden">${window.themeVariables.strings.productSalePrice}</span><span class="price--convertible" data-base-amount="${formatMoney(variant['price'], noCurrencyFormat)}">${formatMoney(variant["price"])}</span></span>`;
          productPrices.innerHTML += `<span class="price price--compare"><span class="visually-hidden">${window.themeVariables.strings.productRegularPrice}</span><span class="price--convertible" data-base-amount="${formatMoney(variant['compare_at_price'], noCurrencyFormat)}">${formatMoney(variant["compare_at_price"], currencyFormat)}</span></span>`;
        } else {
          productPrices.innerHTML += `<span class="price ${this.priceClass}"><span class="visually-hidden">${window.themeVariables.strings.productSalePrice}</span><span class="price--convertible" data-base-amount="${formatMoney(variant['price'], noCurrencyFormat)}">${formatMoney(variant["price"], currencyFormat)}</span></span>`;
        }

        if (variant["taxable"]) {
          productPrices.innerHTML += `
            <div class="vat-message vat-message--product">
              ${window.themeVariables.strings.productVat}
            </div>
            <div class="vat-message vat-message--product vat-message--international">
              ${window.themeVariables.strings.productVatInternational}
            </div>
          `
        }

        if (variant["unit_price_measurement"]) {

          let referenceValue = "";

          if (variant["unit_price_measurement"]["reference_value"] !== 1) {
            referenceValue = `<span class="unit-price-measurement__reference-value">${variant["unit_price_measurement"]["reference_value"]}</span>`;
          }

          productPrices.innerHTML += `
          <div class="price text--subdued ${this.unitPriceClass}">
            <div class="unit-price-measurement">
              <span class="unit-price-measurement__price price--convertible" data-base-amount="${formatMoney(variant['unit_price'], noCurrencyFormat)}">${formatMoney(variant["unit_price"])}</span>
              <span class="unit-price-measurement__separator">/</span>
              ${referenceValue}
              <span class="unit-price-measurement__reference-unit">${variant["unit_price_measurement"]["reference_unit"]}</span>
            </div>
          </div>
        `;
        }

        productPrices.style.display = "";

        currencyConversionUpdatePrices(this);
      }
    }

    _updateLabels(variant) {
      let productLabelList = this.querySelector("[data-product-label-list]");

      if (!productLabelList) {
        return;
      }

      if (!variant) {
        productLabelList.innerHTML = "";
      } else {
        productLabelList.innerHTML = "";
        if (!variant["available"]) {
          productLabelList.innerHTML = `<span class="label label--subdued">${window.themeVariables.strings.collectionSoldOut}</span>`;
        } else if (variant["compare_at_price"] > variant["price"]) {
          let savings = "";
          if (window.themeVariables.settings.discountMode === "percentage") {
            savings = `${Math.round((variant["compare_at_price"] - variant["price"]) * 100 / variant["compare_at_price"])}%`;
          } else {
            savings = formatMoney(variant["compare_at_price"] - variant["price"]);
          }
          productLabelList.innerHTML = `<span class="label label--highlight">${window.themeVariables.strings.collectionDiscount.replace("%savings%", savings)}</span>`;
        }
      }
    }
  };

  window.customElements.define("product-price", ProductPrice);










  /**
   * =============================================================
   * ## PRODUCT PAYMENT CONTAINER
   * =============================================================
   */

  var PaymentContainer = class extends HTMLElement {
    connectedCallback() {
      if (this.discontinued) {
        return;
      }

      var _a;

      (_a = document.getElementById(this.getAttribute("form-id"))) == null ? void 0 : _a.addEventListener("variant:changed", this._onVariantChanged.bind(this));
    }

    get discontinued() {
      return this.hasAttribute("discontinued");
    }

    _onVariantChanged(event) {
      this._updateAddToCartButton(event.detail.variant);
    }

    _updateAddToCartButton(variant) {
      let addToCartButtonElement = this.querySelector("[data-product-add-to-cart-button]");

      // Notify me button is provided by the Back in Stock app
      let notifyMeButtonElement = this.querySelector("[data-product-notify-me-button]");

      if (!addToCartButtonElement) {
        return;
      }

      let addToCartButtonText = "";

      addToCartButtonElement.classList.remove("button--primary", "button--secondary", "button--tertiary");

      if (!variant) {
        notifyMeButtonElement.style.display = "none";
        addToCartButtonElement.setAttribute("disabled", "disabled");
        addToCartButtonElement.classList.add("button--tertiary");
        addToCartButtonText = window.themeVariables.strings.productFormUnavailable;
      } else {
        if (variant["available"]) {
          notifyMeButtonElement.style.display = "none";
          addToCartButtonElement.style.display = "block";
          addToCartButtonElement.removeAttribute("disabled");
          addToCartButtonElement.classList.add("button--primary");
          addToCartButtonText = addToCartButtonElement.getAttribute("data-button-content");
        } else {
          addToCartButtonElement.style.display = "none";
          // Update the notify me button with the new active variant
          notifyMeButtonElement.setAttribute('data-variant-id', variant.id);
          notifyMeButtonElement.style.display = "block";
          addToCartButtonElement.setAttribute("disabled", "disabled");
          addToCartButtonElement.classList.add("button--tertiary");
          addToCartButtonText = window.themeVariables.strings.productFormSoldOut;
        }
      }

      if (addToCartButtonElement.getAttribute("is") === "loader-button") {
        addToCartButtonElement.firstElementChild.innerHTML = addToCartButtonText;
      } else {
        addToCartButtonElement.innerHTML = addToCartButtonText;
      }
    }
  };

  window.customElements.define("product-payment-container", PaymentContainer);









  /**
   * =============================================================
   * ## OVERFLOW SCROLLER
   * =============================================================
   */

  var OverflowScroller = class {
    constructor(element, options) {
      if (!element) {
        return;
      }

      this.element = element;
      this.options = options;
      this.lastKnownY = window.scrollY;
      this.currentTop = 0;
      this.hasPendingRaf = false;
      this.initialTopOffset = options['offsetTop'] || parseInt(window.getComputedStyle(this.element).top);

      window.addEventListener('scroll', this._checkPosition.bind(this));
    }

    _checkPosition() {
      var _this = this;

      if (_this.hasPendingRaf) {
        return;
      }

      _this.hasPendingRaf = true;

      requestAnimationFrame(() => {
        let bounds = _this.element.getBoundingClientRect();
        let maxTop = bounds.top + window.scrollY - _this.element.offsetTop + _this.initialTopOffset;
        let minTop = _this.element.clientHeight - window.innerHeight + (_this.options['offsetBottom'] || 0);

        if (window.scrollY < _this.lastKnownY) {
          _this.currentTop -= window.scrollY - _this.lastKnownY;
        } else {
          _this.currentTop += _this.lastKnownY - window.scrollY;
        }

        _this.currentTop = Math.min(Math.max(_this.currentTop, -minTop), maxTop, _this.initialTopOffset);
        _this.lastKnownY = window.scrollY;
        _this.element.style.top = "".concat(_this.currentTop, "px");
        _this.hasPendingRaf = false;
      });
    }

    destroy() {
      window.removeEventListener('scroll', this._checkPositionListener);
    }
  };










  /**
   * =============================================================
   * ## PRODUCT INFO
   * =============================================================
   */

  var ProductInfo = class extends HTMLElement {
    connectedCallback() {
      var _this = this;
      var productInfoElement = this.querySelector('.product__info-content--sticky');
      var productBlockListWrapper = document.querySelector('.product-block-list__wrapper');

      if (window.matchMedia(window.themeVariables.breakpoints.largeUp).matches && productInfoElement) {
        productBlockListWrapper.style.minHeight = "".concat(productInfoElement.clientHeight, "px");

        this.productInfoResizeObserver = new ResizeObserver(function(event) {
          if (event[0].contentBoxSize) {
            productBlockListWrapper.style.minHeight = "".concat(event[0].contentBoxSize[0].blockSize, "px");
          } else {
            productBlockListWrapper.style.minHeight = "".concat(event[0].contentRect.height, "px");
          }
        });

        this.productInfoResizeObserver.observe(productInfoElement);

        this.infoOverflowScroller = new OverflowScroller(productInfoElement, {
          offsetTop: document.documentElement.style.getPropertyValue('--enable-sticky-header') * parseInt(document.documentElement.style.getPropertyValue('--header-height') + 30),
          offsetBottom: 30
        });
      }
    }
  }

  window.customElements.define("product-info", ProductInfo);










  /**
   * =============================================================
   * ## PRODUCT FORM
   * =============================================================
   */

  var ProductForm = class extends HTMLFormElement {
    connectedCallback() {
      this.masterSelector = this.id;
      this.masterSelector.disabled = false;
      this.submitButtons = Array.from(this.elements).filter((button) => button.type === "submit");

      if (window.themeVariables.settings.cartType === "page" || window.themeVariables.settings.pageType === "cart") {
        return;
      }

      this.addEventListener("submit", this._onSubmit.bind(this));
    }

    get hasOrderLimit() {
      return this.masterSelector.hasAttribute("has-order-limit");
    }

    _disableButtons() {
      this.submitButtons.forEach((submitButton) => {
        submitButton.setAttribute("disabled", "disabled");
        submitButton.setAttribute("aria-busy", "true");
      });
    }

    _enableButtons() {
      this.submitButtons.forEach((submitButton) => {
        submitButton.removeAttribute("disabled");
        submitButton.removeAttribute("aria-busy");
      });
    }

    /**
     * The majority of this function runs validation for our order limits logic.
     * We could have maximum order quantities for a variant or product.
     * This checks if order limits exist, and will check any existing quantity
     * within the cart.
     */
    async _onSubmit(event) {
      event.preventDefault();

      this._disableButtons();

      // JavaScript Form API
      if (!this.checkValidity()) {
        this.reportValidity();
        return;
      }

      var cancelAddToCart = false;

      if (this.hasOrderLimit) {
        var variantId = this.masterSelector.value;

        var maxOrderLimit = parseInt(this.masterSelector.options[this.masterSelector.selectedIndex].getAttribute("data-max-order-limit"));

        // Get the quantity that the customer wants to add and convert it to an integer
        var quantityToAdd = parseInt(this.quantity.value) || 1;

        // This totalQuantity variable can change if we have existing items in the cart
        var totalQuantity = quantityToAdd;

        // Get cart contents to check if we have any existing items and their quantities
        const cartContentsResponse = await fetch(`${window.themeVariables.routes.cartUrl}.js`, {
          method: "GET",
          headers: {
            "X-Requested-With": "XMLHttpRequest"
          }
        });

        const cartContentsResponseJson = await cartContentsResponse.json();

        if (cartContentsResponse.ok) {
          if (cartContentsResponseJson.items.length > 0) {
            cartContentsResponseJson.items.forEach(item => {
              if (item.id == variantId) {

                // If the item is already in the cart, we get it's existing quantity
                // to ensure the total quantity does not exceed an order limit
                var quantityInCart = item.quantity;
                totalQuantity = quantityInCart + quantityToAdd;
              }
            });
          }
        }

        if (!isNaN(maxOrderLimit) && totalQuantity > maxOrderLimit) {
          var maxLimitMessage = window.themeVariables.strings.maximumOrderLimit;
          maxLimitMessage = maxLimitMessage.replace('%moq%', maxOrderLimit);

          window.alert(maxLimitMessage);
          this._enableButtons();
          cancelAddToCart = true;
          return;
        }
      }

      // If a user has broken a limit, we will cancel the add to cart
      if (cancelAddToCart) {
        return;
      }

      this._addToCart(event);
    }


    /**
     * The standard add to cart operation. We assemble form data and use fetch
     * to add to cart.
     */
    async _addToCart(event) {

      const productForm = new FormData(this);

      productForm.append("sections", ["mini-cart"]);;

      productForm.delete("option1");
      productForm.delete("option2");
      productForm.delete("option3");

      const response = await fetch(`${window.themeVariables.routes.cartAddUrl}.js`, {
        body: productForm,
        method: "POST",
        headers: {
          "X-Requested-With": "XMLHttpRequest"
        }
      });

      this._enableButtons();

      const responseJson = await response.json();

      // We dispatch various events which can be tapped into by other functionality
      if (response.ok) {
        this.dispatchEvent(new CustomEvent("variant:added", {
          bubbles: true,
          detail: {
            variant: responseJson.hasOwnProperty("items") ? responseJson["items"][0] : responseJson
          }
        }));

        fetch(`${window.themeVariables.routes.cartUrl}.js`).then(async (response2) => {

          const cartContent = await response2.json();

          document.documentElement.dispatchEvent(new CustomEvent("cart:updated", {
            bubbles: true,
            detail: {
              cart: cartContent
            }
          }));
        });

        document.documentElement.dispatchEvent(new CustomEvent("cart:refresh", {
          bubbles: true,
          detail: {
            cart: responseJson,
            openMiniCart: window.themeVariables.settings.cartType === "drawer"
          }
        }));
      }

      this.dispatchEvent(new CustomEvent("cart-notification:show", {
        bubbles: true,
        cancelable: true,
        detail: {
          status: response.ok ? "success" : "error",
          error: responseJson["description"] || ""
        }
      }));
    }
  };

  window.customElements.define("product-form", ProductForm, { extends: "form" });









  /**
   * =============================================================
   * ## CART COUNT
   * =============================================================
   */

  var CartCount = class extends CustomHTMLElement {
    connectedCallback() {
      this.rootDelegate.on("cart:updated", (event) => this.innerText = event.detail.cart["item_count"]);
    }
  };

  window.customElements.define("cart-count", CartCount);









  /**
   * =============================================================
   * ## LINE ITEM
   * =============================================================
   */

  var LineItem = class extends HTMLElement {
    connectedCallback() {
      this.lineItemLoader = this.querySelector(".line-item__loader");
      this.addEventListener("line-item-quantity:change:start", this._onQuantityStart.bind(this));
      this.addEventListener("line-item-quantity:change:end", this._onQuantityEnd.bind(this));
    }

    _onQuantityStart() {
      if (!this.lineItemLoader || !supportsAnimationsAPI) {
        return;
      }

      this.lineItemLoader.hidden = false;
      this.lineItemLoader.firstElementChild.hidden = false;
      this.lineItemLoader.lastElementChild.hidden = true;
    }

    async _onQuantityEnd(event) {
      if (event.detail.cart["item_count"] === 0) {
        return;
      }

      if (this.lineItemLoader && supportsAnimationsAPI) {
        await this.lineItemLoader.firstElementChild.animate({ opacity: [1, 0], transform: ["translateY(0)", "translateY(-10px)"] }, 75).finished;

        this.lineItemLoader.firstElementChild.hidden = true;

        if (event.detail.newLineQuantity === 0) {
          await this.animate({ opacity: [1, 0], height: [`${this.clientHeight}px`, 0] }, { duration: 300, easing: "ease" }).finished;

          this.remove();
        } else {
          this.lineItemLoader.lastElementChild.hidden = false;

          await this.lineItemLoader.lastElementChild.animate({ opacity: [0, 1], transform: ["translateY(10px)", "translateY(0)"] }, { duration: 75, endDelay: 300 }).finished;

          this.lineItemLoader.hidden = true;
        }
      }
    }
  };

  window.customElements.define("line-item", LineItem);










  /**
   * =============================================================
   * ## LINE ITEM QUANTITY
   * =============================================================
   */

  var LineItemQuantity = class extends CustomHTMLElement {
    connectedCallback() {
      this.delegate.on("click", "a", this._onQuantityLinkClicked.bind(this));
      this.delegate.on("change", "input", this._onQuantityChanged.bind(this));
    }

    get hasOrderLimit() {
      return this.hasAttribute("has-order-limit");
    }

    get currentQuantity() {
      return parseInt(this.getAttribute("data-current-quantity"));
    }

    get maxOrderLimit() {
      return parseInt(this.getAttribute("data-max-order-limit"));
    }

    _alertMaximumQuantityLimit() {
      window.alert("Sorry, you can only order a maximum of " + this.maxOrderLimit + ".");
    }

    _onQuantityLinkClicked(event, target) {
      event.preventDefault();

      if (!this.hasOrderLimit) {
        this._updateFromLink(target.href);
        return;
      } 


      if (target.getAttribute('data-action') === "increase-quantity" && this.maxOrderLimit && this.maxOrderLimit === this.currentQuantity) {
        this._alertMaximumQuantityLimit();
        return;
      }

      this._updateFromLink(target.href);
    }

    _onQuantityChanged(event, target) {
      if (!this.hasOrderLimit) {
        this._updateFromLink(`${window.themeVariables.routes.cartChangeUrl}?quantity=${target.value}&line=${target.getAttribute("data-line")}`);
      } else {

        if (this.maxOrderLimit && target.value > this.maxOrderLimit) {
          this._alertMaximumQuantityLimit();
          target.value = this.currentQuantity;
          return;
        }

        this._updateFromLink(`${window.themeVariables.routes.cartChangeUrl}?quantity=${target.value}&line=${target.getAttribute("data-line")}`);
      }
    }

    async _updateFromLink(link) {
      if (window.themeVariables.settings.pageType === "cart") {
        window.location.href = link;
        return;
      }

      const changeUrl = new URL(link, `https://${window.themeVariables.routes.host}`); 
      const searchParams = changeUrl.searchParams; 
      const line = searchParams.get("line"); 
      const id = searchParams.get("id"); 
      const quantity = parseInt(searchParams.get("quantity"));

      this.dispatchEvent(new CustomEvent("line-item-quantity:change:start", { bubbles: true, detail: { newLineQuantity: quantity } }));
      const response = await fetch(`${window.themeVariables.routes.cartChangeUrl}.js`, {
        body: JSON.stringify({ line, id, quantity, sections: ["mini-cart"] }),
        credentials: "same-origin",
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        }
      });

      const cartContent = await response.json();

      this.dispatchEvent(new CustomEvent("line-item-quantity:change:end", { bubbles: true, detail: { cart: cartContent, newLineQuantity: quantity } }));

      document.documentElement.dispatchEvent(new CustomEvent("cart:updated", {
        bubbles: true,
        detail: {
          cart: cartContent
        }
      }));

      document.documentElement.dispatchEvent(new CustomEvent("cart:refresh", {
        bubbles: true,
        detail: {
          cart: cartContent,
          replacementDelay: quantity === 0 ? 600 : 750
        }
      }));
    }
  };
  
  window.customElements.define("line-item-quantity", LineItemQuantity);










  /**
   * =============================================================
   * ## CART NOTIFICATION
   * =============================================================
   */

  var CartNotification = class extends CustomHTMLElement {
    connectedCallback() {
      this.rootDelegate.on("cart-notification:show", this._onShow.bind(this), !this.hasAttribute("global"));
      this.delegate.on("click", '[data-action="close"]', (event) => {
        event.stopPropagation();
        this.hidden = true;
      });

      this.addEventListener("mouseenter", this.stopTimer.bind(this));
      this.addEventListener("mouseleave", this.startTimer.bind(this));
      window.addEventListener("pagehide", () => this.hidden = true);
    }

    set hidden(value) {
      if (!value) {
        this.startTimer();
      } else {
        this.stopTimer();
      }
      this.toggleAttribute("hidden", value);
    }

    get isInsideDrawer() {
      return this.classList.contains("cart-notification--drawer");
    }

    stopTimer() {
      clearTimeout(this._timeout);
    }

    startTimer() {
      this._timeout = setTimeout(() => this.hidden = true, 3e3);
    }

    _onShow(event) {

      if (this.isInsideDrawer && !this.closest(".drawer").open) {
        return;
      }

      if (this.hasAttribute("global") && event.detail.status === "success" && window.themeVariables.settings.cartType === "drawer") {
        return;
      }

      event.stopPropagation();

      let closeButtonHtml = "";
      if (!this.isInsideDrawer) {
        closeButtonHtml = `
        <button class="cart-notification__close o-tap-area small--hide" data-action="close">
          <span class="visually-hidden">${window.themeVariables.strings.accessibilityClose}</span>
          <svg focusable="false" width="14" height="14" class="icon icon--close icon--inline" viewBox="0 0 14 14">
            <path d="M13 13L1 1M13 1L1 13" stroke="currentColor" stroke-width="2" fill="none"></path>
          </svg>
        </button>
      `;
      }
      if (event.detail.status === "success") {
        this.classList.remove("cart-notification--error");
        this.innerHTML = `
        <div class="cart-notification__overflow">
          <div class="o-container">
            <div class="cart-notification__wrapper">
              <svg focusable="false" width="20" height="20" class="icon icon--cart-notification" viewBox="0 0 20 20">
                <rect width="20" height="20" rx="10" fill="currentColor"></rect>
                <path d="M6 10L9 13L14 7" fill="none" stroke="rgb(var(--color-success))" stroke-width="2"></path>
              </svg>
              
              <div class="cart-notification__text-wrapper">
                <span class="cart-notification__heading heading small--hide">${window.themeVariables.strings.cartItemAdded}</span>
                <span class="cart-notification__heading heading medium-up--hide">${window.themeVariables.strings.cartItemAddedShort}</span>
                <a href="${window.themeVariables.routes.cartUrl}" class="cart-notification__view-cart link">${window.themeVariables.strings.cartViewCart}</a>
              </div>
              
              ${closeButtonHtml}
            </div>
          </div>
        </div>
      `;
      } else {
        this.classList.add("cart-notification--error");
        this.innerHTML = `
        <div class="cart-notification__overflow">
          <div class="o-container">
            <div class="cart-notification__wrapper">
              <svg focusable="false" width="20" height="20" class="icon icon--cart-notification" viewBox="0 0 20 20">
                <rect width="20" height="20" rx="10" fill="currentColor"></rect>
                <path d="M9.6748 13.2798C9.90332 13.0555 10.1763 12.9434 10.4937 12.9434C10.811 12.9434 11.0819 13.0555 11.3062 13.2798C11.5347 13.5041 11.6489 13.7749 11.6489 14.0923C11.6489 14.4097 11.5347 14.6847 11.3062 14.9175C11.0819 15.146 10.811 15.2603 10.4937 15.2603C10.1763 15.2603 9.90332 15.146 9.6748 14.9175C9.45052 14.6847 9.33838 14.4097 9.33838 14.0923C9.33838 13.7749 9.45052 13.5041 9.6748 13.2798ZM9.56689 12.1816V5.19922H11.4141V12.1816H9.56689Z" fill="rgb(var(--color-error))"></path>
              </svg>
              
              <div class="cart-notification__text-wrapper">
                <span class="cart-notification__heading heading">${event.detail.error}</span>
              </div>
              
              ${closeButtonHtml}
            </div>
          </div>
        </div>
      `;
      }
      this.clientHeight;
      this.hidden = false;
    }
  };

  window.customElements.define("cart-notification", CartNotification);










  /**
   * =============================================================
   * ## CART DRAWER
   * =============================================================
   */

  var CartDrawer = class extends DrawerContent {
    connectedCallback() {
      super.connectedCallback();
      this.nextReplacementDelay = 0;
      this.rootDelegate.on("cart:refresh", this._rerenderCart.bind(this));
      this.addEventListener("variant:added", () => this.nextReplacementDelay = 600);
    }

    async _rerenderCart(event) {
      var _a;
      let cartContent = null, html = "";

      if (event.detail && event.detail["cart"]) {
        cartContent = event.detail["cart"];
        html = event.detail["cart"]["sections"]["mini-cart"];
      } else {
        const response = await fetch(`${window.themeVariables.routes.cartUrl}?section_id=${this.getAttribute("section")}`);
        html = await response.text();
      }

      const fakeDiv = document.createElement("div");
      fakeDiv.innerHTML = html;

      setTimeout(async () => {
        var _a2;
        const previousPosition = this.querySelector(".drawer__content").scrollTop;

        this.innerHTML = fakeDiv.querySelector("cart-drawer").innerHTML;

        currencyConversionUpdatePrices(this);

        if (cartContent && cartContent["item_count"] === 0 && supportsAnimationsAPI) {
          this.querySelector(".drawer__content").animate({ opacity: [0, 1], transform: ["translateY(40px)", "translateY(0)"] }, { duration: 450, easing: "cubic-bezier(0.33, 1, 0.68, 1)" });
        } else {
          this.querySelector(".drawer__content").scrollTop = previousPosition;
        }

        if ((_a2 = event == null ? void 0 : event.detail) == null ? void 0 : _a2.openMiniCart) {
          this.clientWidth;
          this.open = true;
        }
      }, ((_a = event == null ? void 0 : event.detail) == null ? void 0 : _a.replacementDelay) || this.nextReplacementDelay);
      this.nextReplacementDelay = 0;
    }

    async attributeChangedCallback(name, oldValue, newValue) {
      super.attributeChangedCallback(name, oldValue, newValue);

      switch (name) {
        case "open":
          if (this.open) {
            this.querySelector(".drawer__content").scrollTop = 0;
          }
      }
    }
  };

  window.customElements.define("cart-drawer", CartDrawer);










  /**
   * =============================================================
   * ## SHIPPING ESTIMATOR
   * =============================================================
   */

  var ShippingEstimator = class extends HTMLElement {
    connectedCallback() {
      this.submitButton = this.querySelector('[type="button"]');
      this.submitButton.addEventListener("click", this._estimateShipping.bind(this));
    }
    async _estimateShipping() {
      const zip = this.querySelector('[name="shipping-estimator[zip]"]').value, country = this.querySelector('[name="shipping-estimator[country]"]').value, province = this.querySelector('[name="shipping-estimator[province]"]').value;
      this.submitButton.setAttribute("aria-busy", "true");
      const prepareResponse = await fetch(`${window.themeVariables.routes.cartUrl}/prepare_shipping_rates.json?shipping_address[zip]=${zip}&shipping_address[country]=${country}&shipping_address[province]=${province}`, { method: "POST" });
      if (prepareResponse.ok) {
        const shippingRates = await this._getAsyncShippingRates(zip, country, province);
        this._formatShippingRates(shippingRates);
      } else {
        const jsonError = await prepareResponse.json();
        this._formatError(jsonError);
      }
      this.submitButton.removeAttribute("aria-busy");
    }
    async _getAsyncShippingRates(zip, country, province) {
      const response = await fetch(`${window.themeVariables.routes.cartUrl}/async_shipping_rates.json?shipping_address[zip]=${zip}&shipping_address[country]=${country}&shipping_address[province]=${province}`);
      const responseAsText = await response.text();
      if (responseAsText === "null") {
        return this._getAsyncShippingRates(zip, country, province);
      } else {
        return JSON.parse(responseAsText)["shipping_rates"];
      }
    }
    _formatShippingRates(shippingRates) {
      var _a;
      (_a = this.querySelector(".shipping-estimator__results")) == null ? void 0 : _a.remove();
      let formattedShippingRates = "";
      shippingRates.forEach((shippingRate) => {
        formattedShippingRates += `<li>${shippingRate["presentment_name"]}: ${formatMoney(parseFloat(shippingRate["price"]) * 100)}</li>`;
      });
      const html = `
      <div class="shipping-estimator__results">
        <p>${shippingRates.length === 0 ? window.themeVariables.strings.shippingEstimatorNoResults : shippingRates.length === 1 ? window.themeVariables.strings.shippingEstimatorOneResult : window.themeVariables.strings.shippingEstimatorMultipleResults}</p>
        ${formattedShippingRates === "" ? "" : `<ul class="unordered-list">${formattedShippingRates}</ul>`}
      </div>
    `;
      this.insertAdjacentHTML("beforeend", html);
    }
    _formatError(errors) {
      var _a;
      (_a = this.querySelector(".shipping-estimator__results")) == null ? void 0 : _a.remove();
      let formattedShippingRates = "";
      Object.keys(errors).forEach((errorKey) => {
        formattedShippingRates += `<li>${errorKey} ${errors[errorKey]}</li>`;
      });
      const html = `
      <div class="shipping-estimator__results">
        <p>${window.themeVariables.strings.shippingEstimatorError}</p>
        <ul class="unordered-list">${formattedShippingRates}</ul>
      </div>
    `;
      this.insertAdjacentHTML("beforeend", html);
    }
  };
  window.customElements.define("shipping-estimator", ShippingEstimator);








  /**
   * =============================================================
   * ## PRODUCT LIST
   * =============================================================
   */

  var ProductList = class extends CustomHTMLElement {
    constructor() {
      super();
      this.productListInner = this.querySelector(".product-list__inner");
      this.productItems = Array.from(this.querySelectorAll("product-item"));
    }

    connectedCallback() {
      this.addEventListener("prev-next:prev", this.previous.bind(this));
      this.addEventListener("prev-next:next", this.next.bind(this));

      // The ability to change layouts is only for product lists in a collection
      if (this.hasLayoutMode) {
        document.documentElement.addEventListener("collection:layout-changed", this.changeLayout.bind(this));

        this.layoutMode = this.getAttribute('data-layout-mode');
      }
    }

    disconnectedCallback() {
      if (this.hasLayoutMode) {
        document.documentElement.removeEventListener("collection:layout-changed", this.changeLayout);
      }
    }

    get hasLayoutMode() {
      return this.hasAttribute("data-layout-mode");
    }

    changeLayout(event) {
      this.setAttribute("data-layout-mode", event.detail.layout);
      document.documentElement.removeEventListener("collection:layout-changed", this.changeLayout);
    }

    previous(event) {
      const columnGap = parseInt(getComputedStyle(this).getPropertyValue("--product-list-column-gap"));

      event.target.nextElementSibling.removeAttribute("disabled");

      event.target.toggleAttribute("disabled", this.productListInner.scrollLeft - (this.productListInner.clientWidth + columnGap) <= 0);

      // 20 is removed pixels to show box shadow as we add 10px padding on
      // each side within the CSS
      this.productListInner.scrollBy({ left: -(this.productListInner.clientWidth + columnGap - 20), behavior: "smooth" });
    }

    next(event) {
      const columnGap = parseInt(getComputedStyle(this).getPropertyValue("--product-list-column-gap"));

      event.target.previousElementSibling.removeAttribute("disabled");

      event.target.toggleAttribute("disabled", this.productListInner.scrollLeft + (this.productListInner.clientWidth + columnGap) * 2 >= this.productListInner.scrollWidth);

      // 20 is removed pixels to show box shadow as we add 10px padding on
      // each side within the CSS
      this.productListInner.scrollBy({ left: (this.productListInner.clientWidth + columnGap - 20), behavior: "smooth" });
    }

  };

  __publicField(ProductList, "observedAttributes", ["hidden"]);

  window.customElements.define("product-list", ProductList);










  /**
   * =============================================================
   * ## COLLECTION LIST
   * =============================================================
   */

  var CollectionList = class extends ProductList {
    constructor() {
      super();
      this.productListInner = this.querySelector(".collection-list__inner");
      this.productItems = Array.from(this.querySelectorAll("collection-list__item"));
    }
  };

  window.customElements.define("collection-list", CollectionList);








  /**
   * =============================================================
   * ## DOM READY
   * =============================================================
   */

  (() => {
    new InputBindingManager();
  })();

  /**
   * Adjusts the `--window-height` CSS variable based on viewport changes 
   */
  (() => {
    let previousClientWidth = window.visualViewport ? window.visualViewport.width : document.documentElement.clientWidth;

    let setViewportProperty = () => {
      const clientWidth = window.visualViewport ? window.visualViewport.width : document.documentElement.clientWidth, clientHeight = window.visualViewport ? window.visualViewport.height : document.documentElement.clientHeight;

      if (clientWidth === previousClientWidth) {
        return;
      }

      requestAnimationFrame(() => {
        document.documentElement.style.setProperty("--window-height", clientHeight + "px");
        previousClientWidth = clientWidth;
      });
    };

    setViewportProperty();

    if (window.visualViewport) {
      window.visualViewport.addEventListener("resize", setViewportProperty);
    } else {
      window.addEventListener("resize", setViewportProperty);
    }
  })();

  /**
   * Form elements feature a floating label which requires an "is-filled" class
   */
  (() => {
    let documentDelegate = new delegate_default(document.body);
    documentDelegate.on("keyup", 'input:not([type="checkbox"]):not([type="radio"]), textarea', (event, target) => {
      target.classList.toggle("is-filled", target.value !== "");
    });
    documentDelegate.on("change", "select", (event, target) => {
      target.parentNode.classList.toggle("is-filled", target.value !== "");
    });
  })();


  /**
   * Utilities for the rich text editor which standardise tables and videos for
   * easier styling. 
   */
  (() => {
    document.querySelectorAll(".rte table").forEach((table) => {
      table.outerHTML = '<div class="table-wrapper">' + table.outerHTML + "</div>";
    });

    document.querySelectorAll(".rte iframe").forEach((iframe) => {
      if (iframe.src.indexOf("youtube") !== -1 || iframe.src.indexOf("youtu.be") !== -1 || iframe.src.indexOf("vimeo") !== -1) {
        iframe.outerHTML = '<div class="video-wrapper">' + iframe.outerHTML + "</div>";
      }
    });
  })();

  /**
   * Tabbing utility to add focus to elements for improved accessibility
   */
  (() => {
    document.addEventListener("keyup", function(event) {
      if (event.key === "Tab") {
        document.body.classList.remove("no-focus-outline");
        document.body.classList.add("focus-outline");
      }
    });
  })();